import { AlertBanner, type AlertSectionLink } from '@/components/common/AlertBanner'
import type { HPOTerm } from '@/components/common/HPOSearch'
import { ClinicalIndicationsModal } from '@/components/modals/ClinicalIndicationsModal'
import { ComparatorModal } from '@/components/modals/ComparatorModal'
import { ConfirmDialog } from '@/components/modals/ConfirmDialog'
import { GuardianContactModal } from '@/components/modals/GuardianContactModal'
import { FamilyHistorySummaryModal } from '@/components/modals/FamilyHistorySummaryModal'
import { TestOptionsModal } from '@/components/modals/TestOptionsModal'
import { NewOrderModal, type OrderFormData } from '@/components/NewOrderModal'
import { Navbar } from '@/components/Navbar'
import { ClinicalSummaryCard } from '@/components/order/ClinicalSummaryCard'
import {
  ClinicalSummaryEditor,
  type ClinicalSummaryEditorControls,
  type ClinicalSummarySnapshot,
} from '@/components/order/ClinicalSummaryEditor'
import { ComparatorList } from '@/components/order/ComparatorList'
import { ComparatorCard } from '@/components/order/ComparatorCard'
import { ComparatorSideNavModals } from '@/components/order/ComparatorSideNavModals'
import { FamilyHistoryCard } from '@/components/order/FamilyCard'
import { GuardianContactCard } from '@/components/order/GuardianContactCard'
import { guardianContactCardFieldsFromCanonical } from '@/components/order/guardianContactCardFields'
import { OrderHeader } from '@/components/order/OrderHeader'
import { OrderSideNav, OrderNavToggle } from '@/components/order/OrderSideNav'
import {
  PROBAND_INCIDENTAL_FINDINGS_ANCHOR_ID,
  isKitOrderingEnabled,
  isOrderSideNavEnabled,
  useOrderSideNavController,
  useOrderStickyTop,
} from '@/pages/order/orderSideNavModel'
import { ComparatorDetailView } from '@/components/order/ComparatorDetailView'
import { TrashBin, UserAdd } from 'flowbite-react-icons/outline'
import type { ClinicalSummaryData, FamilyHistorySummaryData, FamilyMember, TestOptions } from '@/components/order/OrderTypes'
import {
  comparatorConsentAnyApplicable,
  incidentalConsentGated,
  resolveConsentInteractionGates,
} from '@/components/order/comparatorParentConsentConfig'
import { ProbandConsentSections } from '@/components/order/ProbandConsentSections'
import { PatientConsentCard } from '@/components/order/PatientConsentCard'
import { TestOptionsCard } from '@/components/order/TestOptionsCard'
import {
  formatTurnaroundTimeSequencingCardSubtitle,
  getClientName,
  getDefaultTestType,
  getLabName,
  getMaxParentSamplesForCategory,
  getPatientConsents,
  getTestTypeConfig,
  getFulfillmentStrategy,
  getPhenotypeModel,
  getTurnaroundTimeDayBasisForTestType,
  isOrderSiteTrackingEnabled,
  getOrganizationSiteNames,
  isValidOrganizationSite,
  isTrfSignatureDocusignEnabled,
  resolveTestTypeForCategoryTier,
  type UiConfig,
} from '@/config/uiConfig'
import { useUiConfig } from '@/config/uiConfigHooks'
import {
  applyHiddenSecondaryConsentDefaults,
  useEnforceNonApplicableSecondaryConsentDefaults,
} from '@/hooks/useEnforceNonApplicableSecondaryConsentDefaults'
import { useConfiguredOrderSteps } from '@/hooks/useConfiguredOrderSteps'
import { copyWithoutSigning } from '@/utils/copyWithoutSigning'
import ProviderAPIClient, { OrderingSessionGoneError } from '@/features/auth/ProviderAPIClient'
import EpicOrderDiscarded from '@/features/auth/components/EpicOrderDiscarded'
import { EpicAuthClient } from '@/features/auth/services/EpicAuthClient'
import { EpicSessionExpiryWarning } from '@/features/auth/components/EpicSessionExpiryWarning'
import { isDiscardMarkerSet } from '@/features/ordering/discardMarker'
import { DiscardOrderConfirmDialog } from '@/components/modals/DiscardOrderConfirmDialog'
import { RestartOrderModal, type RestartOrderMode } from '@/components/modals/RestartOrderModal'
import { offerRestartOfCancelledOrder, restartFailureOutcome } from '@/ordering/restartCancelledOrder'
import type { OrderRestartEligibility } from '@/utils/DatabaseOperations'
import { useDiscardOrder } from '@/pages/order/hooks/useDiscardOrder'
import { useLaunchMode } from '@/features/auth/launchContext'
import { useAuthorization } from '@/features/authorization'
import { hpoAPIClient } from '@/features/terminology/hpo/HPOAPIClient'
import { resolveHpoLabel } from '@/features/terminology/hpo/hpoLabelResolver'
import { mapWithConcurrency } from '@/features/terminology/hpo/hpoLookupCache'
import { isHpoCode } from '@/features/terminology/hpo/hpoTypes'
import { useResolveIndicationHpoLabels } from './hooks/useResolveIndicationHpoLabels'
import { pulseElementById } from './orderSectionPulse'
import {
  parentSectionAnchorIdForRelationship,
  parentSectionLabelForRelationship,
} from './orderInProgressSectionLinks'
import { resolveEffectiveConsentFlowForSubmit, testLinesFromUiConfig } from '@/pages/order/orderingSubmitValidation'
import {
  clinicalTextLimitError,
  comparatorClinicalNoteBudget,
  probandClinicalTextBudget,
} from '@/utils/freeTextValidation'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import {
  mergeClinical,
  mergeFamily,
  mergeOrderSelection,
  mergePatient,
  mergeReason,
  setClinicalSummaryReview,
  resetCanonicalOrder,
  setCanonicalOrder,
  removeComparator,
  setGuardianContact,
  setOrderType,
  setOrderingSite,
  upsertComparator,
} from '@/store/slices/canonicalOrderSlice'
import { useStore } from 'react-redux'
import type { RootState } from '@/store'
import {
  type CategorizedHPOOption,
  CollectionType,
  OrderFormInfoData,
  type PartialRequisitionFormData,
  Sex,
  TestType,
} from '@/types/RequisitionFormTypes'
import { checkResourceScope } from '@/features/auth/services/resourceScopeApi'
import { lookupGuardianContact } from '@/services/guardianContactLookup'
import { getOrderById } from '@/utils/DatabaseOperations'
import { parentSideForComparator } from '@/ordering/comparatorWriteAdapter'
import {
  ADD_FAMILY_MEMBER_RELATIONSHIP_OPTIONS,
  formatComparatorRelationshipLabel,
  relationshipOptionsForStrategy,
} from '@/ordering/comparatorRelationshipOptions'
import {
  canonicalHasProviderAuthoredContent,
  carryForwardDraftContentOntoCanonical,
  guardianContactHasSubstantiveContact,
  initCanonicalFromOrderFormInfo,
  legacyTrfPayloadToCanonical,
  normalizeCanonicalOrderForSessionPersist,
  mapCanonicalToOrderFormInfo,
  mapEpicContextToEhrContext,
  mapSampleTypeToCollectionType,
  type CanonicalGuardianContact,
  type CanonicalOrderPayload,
  type Comparator,
  type FamilyMemberRelationship,
} from '@/ordering/canonicalOrder'
import { deriveOrderInProgressFamilyRows } from '@/ordering/orderInProgressFamilyCards'
import { projectComparatorsToOrderInProgressRows } from '@/ordering/comparatorsToOrderInProgressRows'
import { orderHasGuardianContact, resolveComparatorPlatformCollects } from '@/ordering/comparatorPlatformConsent'
import { getParentComparator, orderTypeFromComparators } from '@/ordering/comparatorReadAdapter'
import {
  selectionHasRelationship,
  withRelationshipSelection,
  withRelationshipSelections,
} from '@/ordering/testOptionsRelationships'
import {
  applySequencingOptionsChangeToCanonical,
  canonicalParentToFamilyMember,
  deriveBiologicalParentsFromCanonical,
} from '@/ordering/sequencingComparatorUpgrade'
import { FormCompletionChecker } from '@/utils/FormCompletionChecker'
import { extractFormOrderData } from '@/utils/FormDataExtraction'
import {
  readEpicLaunchPatientId,
  readSessionPatientLookupId,
  resolvePatientIdForDraftLookup,
} from '@/utils/orderDraftPatientId'
import { resolveImdbOrgIdForOrderingApi } from '@/utils/orderingOrgId'
import {
  formatAge,
  formatFamilyHistoryDetails,
  getPatientInfoFromForm,
  mapCategoryToType,
  mapCollectionStringToSampleTypeStrict,
  mapCollectionTypeToSampleType,
  parseDateOnlyToLocalDate,
  type SampleTypeOption,
} from '@/utils/orderFormUtils'
import { useAuth0 } from '@auth0/auth0-react'
import * as Sentry from '@sentry/react'
import { useAutosave } from '@/components/common/useAutosave'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import type { ClinicalSummaryReview } from './clinicalSummaryReviewTypes'
import { ClinicalSummaryDraftQueue, DraftQueueInvalidatedError } from './clinicalSummaryDraftQueue'
import { blocksSubmit, reviewForUnreviewedDiagnosis } from './livePhenotypeMerge'
import {
  clinicalSummaryDataFromCanonical,
  indicationTermsFromCanonical,
  indicationsAttentionTarget,
  sideNavSectionsForMergedClinicalSummary,
} from './mergedClinicalSummaryState'


/** Dev-only: filter console with `[OrderStart` to trace hydration / Redux / test type. */
function traceOrderStart(phase: string, data?: Record<string, unknown>) {
  if (!import.meta.env.DEV) return
  console.log(`[OrderStart:${phase}]`, { ...data, _ts: Date.now() })
}

/**
 * Session often stores `selectedOption` as "" (same as TestType.NONE). `??` does not treat
 * "" as missing, so without this we skip org default on hydrate and the card stays empty.
 */
function sessionStoredTestTypeOrMissing(raw: string | null | undefined): TestType | undefined {
  if (raw == null) {
    return undefined
  }
  const trimmed = String(raw).trim()
  if (!trimmed) {
    return undefined
  }
  return raw as TestType
}

/**
 * New order: trio defaults both parent comparators on; duo/proband-only default
 * neither until the user chooses. Returns the relationship list seed for
 * `TestOptions.selectedComparatorRelationships` (P12: relationship-first).
 */
function initialComparatorRelationshipsForOrderedTest(
  testType: TestType | '',
  uiConfig: UiConfig
): FamilyMemberRelationship[] {
  if (!testType) return []
  const pr = getTestTypeConfig(testType as TestType, uiConfig)?.parentSamplesRequired ?? 0
  return pr >= 2 ? ['MOTHER', 'FATHER'] : []
}

/**
 * Default proband sample type for a freshly initialized form: when the test type's
 * ``probandSampleTypes`` allowlist has exactly one entry, pre-select it; otherwise
 * leave undefined so the user must pick (mirrors ``TestOptionsModal`` behavior).
 */
function initialProbandSampleTypeForOrderedTest(
  testType: TestType | '',
  uiConfig: UiConfig
): TestOptions['sampleType'] {
  if (!testType) {
    return undefined
  }
  const configured = getTestTypeConfig(testType as TestType, uiConfig)?.probandSampleTypes ?? []
  const allowed: NonNullable<TestOptions['sampleType']>[] = []
  const seen = new Set<NonNullable<TestOptions['sampleType']>>()
  for (const raw of configured) {
    const mapped = mapCollectionStringToSampleTypeStrict(String(raw))
    if (mapped && !seen.has(mapped)) {
      seen.add(mapped)
      allowed.push(mapped)
    }
  }
  if (allowed.length === 1) {
    return allowed[0]
  }
  if (allowed.length === 0) {
    return 'whole-blood'
  }
  return undefined
}

/** One network GET per sessionId even if StrictMode or effects run concurrently (shared promise). */
const orderingSessionGetInFlight = new Map<string, Promise<Record<string, unknown>>>()

function getOrderingSessionCoalesced(sessionId: string): Promise<Record<string, unknown>> {
  const hit = orderingSessionGetInFlight.get(sessionId)
  if (hit) {
    traceOrderStart('session GET coalesce', { sessionId, note: 'reuse in-flight promise' })
    return hit
  }
  traceOrderStart('session GET start', { sessionId })
  const client = ProviderAPIClient.getInstance()
  const promise = client.getOrderingSession(sessionId).finally(() => {
    orderingSessionGetInFlight.delete(sessionId)
    traceOrderStart('session GET map cleared', { sessionId })
  })
  orderingSessionGetInFlight.set(sessionId, promise)
  return promise
}

/**
 * True when a canonical is still an UNTOUCHED Epic launch seed (no provider-authored
 * content). Field-agnostic correctness backstop on the deferred Epic create path: a
 * bare launch seed must never be persisted as an `order_draft` (the confirmed
 * production regression where a mount-time system reconcile fired a create of an
 * untouched seed). Exact negation of `canonicalHasProviderAuthoredContent` so
 * "touched" cannot drift from the submit gate (arch-review D-SF4); see that
 * predicate for the `selectedOptionIsUntouchedConfigDefault` (D1) and
 * `consentApplicability` (D-1) nuances. Both default to the conservative behavior,
 * leaving standalone / resume callers unchanged.
 */
// Exported (module-level) so the deferred-create guard tests assert against the
// REAL predicate rather than a drift-prone copy. Pure helper, not a component.
// eslint-disable-next-line react-refresh/only-export-components
export function isUntouchedLaunchSeed(
  canonical: CanonicalOrderPayload | null | undefined,
  guardianUserTouched: boolean,
  selectedOptionIsUntouchedConfigDefault = false,
  consentApplicability?: { ny?: boolean; research?: boolean }
): boolean {
  if (!canonical) return false
  return !canonicalHasProviderAuthoredContent(canonical, {
    guardianUserTouched,
    selectedOptionIsUntouchedConfigDefault,
    consentApplicability,
  })
}

const CONFLICT_RELOAD_FAILED_MESSAGE =
  'This draft changed elsewhere and could not be reloaded. Refresh the page to continue.'

export function OrderStart() {
  const { config, reloadByConfigId } = useUiConfig()
  // Latch the clinical-note attachments flag: once the loaded config reports it
  // on, keep it on for the rest of this screen. A config reload (e.g.
  // reloadByConfigId, or the initial dev client-config fetch) briefly yields an
  // empty config where `featureFlags.clinicalNoteAttachments` is undefined; a
  // raw read would then flip the uploader's mount off and abort an in-flight
  // upload. Latching makes the uploader's mount stable across those blips.
  const [clinicalNoteAttachmentsEnabled, setClinicalNoteAttachmentsEnabled] = useState(false)
  useEffect(() => {
    if (config.featureFlags?.clinicalNoteAttachments) setClinicalNoteAttachmentsEnabled(true)
  }, [config.featureFlags?.clinicalNoteAttachments])
  const { orgId, userId: currentUserId, isLoading: authorizationLoading } = useAuthorization()
  const { user: auth0User, logout: auth0Logout } = useAuth0()
  const { mode: launchMode, epicState: persistedEpicState } = useLaunchMode()
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  // Type for location state
  interface LocationState {
    epicState?: string
    patientData?: OrderFormInfoData
    authFlow?: string
    orderId?: string
    sessionId?: string | null
    /** GEN-5738: ordering site chosen in the New Order modal (standalone create). */
    orderingSite?: string
  }

  const locationState = location.state as LocationState | string | null | undefined
  // Resolve Epic OAuth state: prefer navigation state (in-memory), then persisted sessionStorage value
  // (page reload / deep link). This ensures EpicAuthClient can always locate the correct session.
  const state =
    (typeof locationState === 'object' && locationState !== null ? locationState.epicState : undefined) ||
    (typeof locationState === 'string' ? locationState : '') ||
    persistedEpicState ||
    ''

  // Get standalone patient data if passed from Orders page
  const standalonePatientData =
    typeof locationState === 'object' && locationState !== null ? locationState.patientData : undefined
  const standaloneAuthFlow =
    typeof locationState === 'object' && locationState !== null ? locationState.authFlow : undefined
  const standaloneOrderId =
    typeof locationState === 'object' && locationState !== null ? locationState.orderId : undefined

  // Session ID: prefer navigation state (back-nav), then URL param (refresh), otherwise null (new order)
  const initialSessionId =
    (typeof locationState === 'object' && locationState !== null ? locationState.sessionId : null) ??
    searchParams.get('sessionId')
  const [sessionId, setSessionId] = useState<string | null>(initialSessionId)
  /** False on load when URL has sessionId until GET/remount-restore finishes — blocks canonical-sync from clobbering session test selection. */
  const [sessionHydrationDone, setSessionHydrationDone] = useState(() => !initialSessionId)

  // Detect authentication flow: use launch mode as source of truth (survives page reload),
  // falling back to location.state.authFlow when explicitly set (e.g. standalone order open).
  const authFlow = standaloneAuthFlow || (launchMode === 'epic_smart' ? 'epic' : 'standalone')

  const hasFetchedPatient = useRef(false)
  // Guardian contact fetched from Epic FHIR during the initial patient-data load
  // (alongside `extractFormOrderData`). Held here until the ordering session +
  // canonical exist, then applied via `setGuardianContact` in the create/resume
  // session effect. `null` = fetched, none found / not applicable.
  const pendingEpicGuardianRef = useRef<CanonicalGuardianContact | null>(null)
  const isCreatingSession = useRef(false)
  // GEN-6020: Restart Order prompt raised by the create/resume effect; the
  // effect awaits the provider's choice through `restartChoiceRef`.
  const [restartPrompt, setRestartPrompt] = useState<OrderRestartEligibility | null>(null)
  const restartChoiceRef = useRef<((mode: RestartOrderMode | null) => void) | null>(null)
  const resolveRestartChoice = useCallback((mode: RestartOrderMode | null) => {
    const resolve = restartChoiceRef.current
    restartChoiceRef.current = null
    setRestartPrompt(null)
    resolve?.(mode)
  }, [])
  /**
   * Deferred-create state for the Epic/EHR launch flow. To avoid persisting a
   * distinct order for a patient who may never qualify, an Epic launch that
   * finds no existing draft does NOT immediately POST /ordering/sessions.
   * Instead it seeds Redux locally and "arms" a deferred create here; the actual
   * POST is triggered lazily on the first substantive user edit (see the dirty-
   * watch effect) or when the provider advances to the review page
   * (`handleSubmitOrder`). Standalone flow is unchanged (still creates eagerly).
   *
   * - `pendingCreateArmedRef`: true once a launch seeded the form locally without
   *   creating a server session, so a create is owed on first real edit.
   * - `deferredCreateBaselineRef`: normalized JSON of the seeded launch canonical.
   *   The dirty-watch effect compares the live canonical against this baseline to
   *   decide whether the provider has made a persist-worthy change yet.
   */
  const pendingCreateArmedRef = useRef(false)
  const deferredCreateBaselineRef = useRef<string | null>(null)
  /** True while a `performCreate` POST is in flight, so no concurrent trigger issues a second POST. */
  const createInFlightRef = useRef(false)
  /**
   * Holds the in-flight `performCreate` promise so a concurrent caller (e.g. the
   * review CTA racing the dirty-watch effect) can AWAIT the underway create and
   * receive its sessionId, instead of getting a spurious null and aborting.
   */
  const createInFlightPromiseRef = useRef<Promise<string | null> | null>(null)
  /** Identity token for the current in-flight create, so its finally clears the
   *  shared promise ref only if a later retry hasn't already replaced it. */
  const createInFlightTokenRef = useRef<object | null>(null)
  /** Set by the create-session effect so imperative callers (e.g. review CTA) can force the deferred create. */
  const createDeferredSessionRef = useRef<(() => Promise<string | null>) | null>(null)
  /**
   * D4 (GEN-5383 arch-review): the create→session reconcile. An edit landing in the
   * narrow window between the in-function flush snapshot and React committing
   * `setSessionId` (which enables autosave — which does NOT fire on the enabling
   * transition alone) would otherwise be neither flushed nor autosaved. These refs
   * drive a one-shot post-create PATCH that closes it:
   * - `locallyCreatedSessionIdRef`: sessionId created by THIS client (not resumed),
   *   the only kind eligible for the reconcile.
   * - `lastPersistedCreatePayloadRef`: normalized JSON the create path last
   *   persisted; the reconcile PATCHes only when the committed store differs.
   * - `createReconcileDoneRef`: fires the reconcile at most once per session.
   */
  const locallyCreatedSessionIdRef = useRef<string | null>(null)
  const lastPersistedCreatePayloadRef = useRef<string | null>(null)
  const createReconcileDoneRef = useRef<string | null>(null)
  /**
   * Bumped once in `performCreate`'s finally (after `createInFlightRef` clears) so
   * the create→session reconcile effect re-runs after the create fully settles. The
   * effect can run DURING the create (React may flush the `setSessionId` re-render
   * while the awaited flush / `lockPinnedConfig` are still pending), when it bails on
   * the `createInFlightRef` guard; without this tick nothing would re-render once the
   * create finished and a window edit would never be PATCHed.
   */
  const [createSettledTick, setCreateSettledTick] = useState(0)
  /**
   * True once the provider has touched the Guardian Contact section on the
   * deferred (not-yet-persisted) Epic path. The Epic prefill is only re-applied
   * at create time when the provider has NOT touched it — so a deliberate clear
   * is never silently reverted by `withPendingEpicGuardian`.
   */
  const guardianUserTouchedRef = useRef(false)
  /** Microapp config doc id already locked for this order; dedupes reload calls. */
  const pinnedConfigIdRef = useRef<string | null>(null)
  /** Last test type from session GET / remount-restore, or last user commit from Test Options modal (cleared when user picks no test). */
  const sessionLoadedTestTypeRef = useRef<string | null>(null)
  const dispatch = useAppDispatch()
  const { canonicalOrder: storedCanonical, isHydrated } = useAppSelector((s) => s.canonicalOrder)

  /**
   * Microapp config version — forwarded to OrderReadyToSend for submit and used as the
   * session ``configVersion`` lock. Sourced from ``UiConfig.version`` (the microapp version).
   */
  const domainConfigVersion = useMemo(() => config.version || '', [config.version])

  /**
   * Lock the client config the server pinned to this draft so the form stays on
   * that revision for the life of the order (create, resume, and refresh paths).
   * Best-effort: any failure leaves the live config in place — submit still uses
   * the server-side pin, so a failed reload never blocks ordering.
   */
  const lockPinnedConfig = useCallback(
    async (rawConfigId: unknown): Promise<void> => {
      if (!reloadByConfigId) {
        return
      }
      const pinnedConfigId = typeof rawConfigId === 'string' ? rawConfigId.trim() : ''
      if (!pinnedConfigId || pinnedConfigIdRef.current === pinnedConfigId) {
        return
      }
      try {
        const orgForConfig = await resolveImdbOrgIdForOrderingApi({
          orgIdFromAuth: orgId,
          isEpicFlow: launchMode === 'epic_smart',
          epicOAuthState: state || undefined,
        })
        if (!orgForConfig) {
          return
        }
        const applied = await reloadByConfigId(orgForConfig, pinnedConfigId)
        if (applied) {
          pinnedConfigIdRef.current = pinnedConfigId
        }
      } catch (lockErr) {
        Sentry.captureException(lockErr, { tags: { operation: 'order.config.lock_pinned' } })
      }
    },
    [reloadByConfigId, orgId, launchMode, state]
  )
  /** Submit catalog (code → consentFlowId) derived from ``UiConfig.testTypes``. */
  const orderingDomainTestLines = useMemo(() => testLinesFromUiConfig(config), [config])
  const effectiveConsentFlowForSubmit = useMemo(
    () =>
      resolveEffectiveConsentFlowForSubmit(
        config,
        storedCanonical?.order?.selectedOption ?? '',
        orderingDomainTestLines
      ),
    [config, storedCanonical?.order?.selectedOption, orderingDomainTestLines]
  )

  const autosaveAbortRef = useRef<AbortController | null>(null)
  const [clinicalSummaryEditorEpoch, setClinicalSummaryEditorEpoch] = useState(0)
  const latestClinicalSummaryEditorEpoch = useRef(clinicalSummaryEditorEpoch)
  latestClinicalSummaryEditorEpoch.current = clinicalSummaryEditorEpoch
  const mergedDraftQueueRef = useRef<{
    sessionId: string
    queue: ClinicalSummaryDraftQueue<{ canonical: CanonicalOrderPayload; signal: AbortSignal }>
  } | null>(null)
  const mergedDraftReloadGenerationRef = useRef(0)
  /** Set from a 409 until the server copy is applied; drafts taken meanwhile would overwrite it. */
  const mergedDraftConflictReloadingRef = useRef(false)
  const handleSessionGoneRef = useRef<(attribution?: string | null) => void>(() => {})
  const resetIndicationLabelResolutionRef = useRef<() => void>(() => {})

  const autoSaveCanonical = useCallback(
    async (canonical: CanonicalOrderPayload | null) => {
      if (!sessionId || !canonical) return false
      autosaveAbortRef.current?.abort()
      const ac = new AbortController()
      autosaveAbortRef.current = ac
      if (mergedDraftConflictReloadingRef.current) return false
      const providerClient = ProviderAPIClient.getInstance()
      try {
        if (!mergedDraftQueueRef.current || mergedDraftQueueRef.current.sessionId !== sessionId) {
          const queue = new ClinicalSummaryDraftQueue<{ canonical: CanonicalOrderPayload; signal: AbortSignal }>(
            async ({ canonical: queuedCanonical, signal }) => {
              if (mergedDraftConflictReloadingRef.current) throw new DraftQueueInvalidatedError()
              try {
                await providerClient.patchOrderingSession(
                  sessionId,
                  { state: { canonicalOrder: normalizeCanonicalOrderForSessionPersist(queuedCanonical) } },
                  signal
                )
              } catch (error) {
                if (error instanceof Error && error.message.includes('patch failed: 409')) {
                  mergedDraftConflictReloadingRef.current = true
                  mergedDraftReloadGenerationRef.current += 1
                }
                throw error
              }
            }
          )
          mergedDraftQueueRef.current = { sessionId, queue }
        }
        await mergedDraftQueueRef.current.queue.enqueue({ canonical, signal: ac.signal })
        return true
      } catch (err) {
        if (err instanceof DraftQueueInvalidatedError) return false
        if (err instanceof OrderingSessionGoneError) {
          handleSessionGoneRef.current()
          return false
        }
        if (err instanceof Error && err.message.includes('patch failed: 409')) {
          mergedDraftQueueRef.current?.queue.invalidate()
          try {
            const session = await providerClient.getOrderingSession(sessionId)
            const state = session['state'] as Record<string, unknown> | undefined
            const current = state?.['canonicalOrder'] as CanonicalOrderPayload | undefined
            if (!current) throw new Error('The ordering session that won the conflict has no canonical order')
            dispatch(setCanonicalOrder(current))
            setClinicalSummary(clinicalSummaryDataFromCanonical(current))
            resetIndicationLabelResolutionRef.current()
            setIndications({ phenotypes: indicationTermsFromCanonical(current) })
            mergedDraftQueueRef.current = null
            mergedDraftConflictReloadingRef.current = false
            setClinicalSummaryEditorEpoch((epoch) => epoch + 1)
            setOrderActionError('This draft changed elsewhere. The latest clinical summary was reloaded; please retry.')
          } catch (reloadError) {
            // Saving stays held: writing the local copy now would overwrite the other change unseen.
            Sentry.captureException(reloadError, { tags: { operation: 'order.merged_summary_conflict_reload' } })
            setOrderActionError(CONFLICT_RELOAD_FAILED_MESSAGE)
          }
          return false
        }
        if (err instanceof DOMException && err.name === 'AbortError') return false
        Sentry.captureException(err, { tags: { operation: 'order.autosave' } })
        return false
      }
    },
    [dispatch, sessionId]
  )

  const {
    cancelPendingSave,
    markSaved: markCanonicalSaved,
    lastSaveTime: draftLastSaveTime,
    isSaving: draftIsSaving,
    saveFailed: draftSaveFailed,
  } = useAutosave(
    storedCanonical,
    autoSaveCanonical,
    3000,
    sessionHydrationDone && Boolean(sessionId) && Boolean(storedCanonical)
  )

  const cancelAutosave = useCallback(() => {
    cancelPendingSave()
    autosaveAbortRef.current?.abort()
    autosaveAbortRef.current = null
  }, [cancelPendingSave])

  const [showBanner, setShowBanner] = useState(true)
  const [showReadyBanner, setShowReadyBanner] = useState(true)
  const [patientInfo, setPatientInfo] = useState(() => getPatientInfoFromForm(null))
  const [orderFormInfoData, setOrderFormInfoData] = useState<OrderFormInfoData | null>(null)
  const [draftSessionMeta, setDraftSessionMeta] = useState<{
    ownerUserId: string | null
    updatedAt: string | null
    patientLookupId: string | null
  }>({ ownerUserId: null, updatedAt: null, patientLookupId: null })
  const [draftSessionMetaLoaded, setDraftSessionMetaLoaded] = useState(() => !initialSessionId)

  // The session record supplies the patient id until the order form hydrates, so a
  // discard right after a refresh still writes the marker for the right patient.
  const patientLookupId = useMemo(
    () =>
      (orderFormInfoData
        ? resolvePatientIdForDraftLookup({
            launchMode,
            orderFormInfoData,
            epicOAuthState: state,
          })
        : null) ?? draftSessionMeta.patientLookupId,
    [draftSessionMeta.patientLookupId, launchMode, orderFormInfoData, state]
  )

  const clearLocalOrderContext = useCallback(() => {
    setSessionId(null)
    setOrderFormInfoData(null)
    setPatientInfo(getPatientInfoFromForm(null))
    setDraftSessionMeta({ ownerUserId: null, updatedAt: null, patientLookupId: null })
    setSearchParams({}, { replace: true })
    navigate(location.pathname, { replace: true, state: null })
  }, [location.pathname, navigate, setSearchParams])

  const {
    confirmOpen: discardConfirmOpen,
    isDiscarding,
    discardError,
    epicDiscarded,
    draftOwnerDisplayName,
    openDiscardConfirm,
    closeDiscardConfirm,
    performDiscard,
    handleSessionGone,
    clearDiscardError,
    confirmReady,
  } = useDiscardOrder({
    sessionId,
    patientLookupId,
    authFlow: authFlow === 'epic' ? 'epic' : 'standalone',
    draftOwnerUserId: draftSessionMeta.ownerUserId,
    currentUserId,
    cancelAutosave,
    onClearLocalOrderContext: clearLocalOrderContext,
    draftOwnerMetadataPending: Boolean(sessionId) && !draftSessionMetaLoaded,
  })

  handleSessionGoneRef.current = handleSessionGone

  useEffect(() => {
    if (!sessionId) {
      setDraftSessionMeta({ ownerUserId: null, updatedAt: null, patientLookupId: null })
      setDraftSessionMetaLoaded(true)
      return
    }
    setDraftSessionMetaLoaded(false)
    let cancelled = false
    void ProviderAPIClient.getInstance()
      .getOrderingSession(sessionId)
      .then((session) => {
        if (cancelled) return
        setDraftSessionMeta({
          ownerUserId: typeof session['userId'] === 'string' ? session['userId'] : null,
          updatedAt: typeof session['updatedAt'] === 'string' ? session['updatedAt'] : null,
          patientLookupId: readSessionPatientLookupId(session),
        })
        setDraftSessionMetaLoaded(true)
      })
      .catch((err) => {
        if (cancelled) return
        if (err instanceof OrderingSessionGoneError) {
          handleSessionGoneRef.current()
        }
        setDraftSessionMetaLoaded(true)
      })
    return () => {
      cancelled = true
    }
  }, [sessionId])

  const [patientError, setPatientError] = useState<string | null>(null)
  const [orderActionError, setOrderActionError] = useState<string | null>(null)
  const [comparatorRemovalConfirmOpen, setComparatorRemovalConfirmOpen] = useState(false)
  // Snapshot of the comparator being removed, captured when the confirm dialog
  // opens, so a mid-dialog selection change can't retarget the removal.
  const [comparatorPendingRemoval, setComparatorPendingRemoval] = useState<{
    id: string
    comparator: Comparator
  } | null>(null)
  const [isSubmittingForReview, setIsSubmittingForReview] = useState(false)

  const [testOptionsModalOpen, setTestOptionsModalOpen] = useState(false)
  const [clinicalModalOpen, setClinicalModalOpenState] = useState(false)
  const [familyHistorySummaryModalOpen, setFamilyHistorySummaryModalOpen] = useState(false)
  // F.1c (GEN-2999): canonical Comparator edit state. Populated when the user
  // clicks an edit pencil on a `<ComparatorCard>` inside `<ComparatorList>`, or
  // via a validation fix-me link / guardian-modal "edit parent" callback
  // (`openComparatorEditorForParent`). Mounts `<ComparatorModal>`. This is the
  // sole parent edit path now. P8 (MOTHER/FATHER migration): editor state is
  // keyed by the comparator itself (which carries `id` + `relationship`); the
  // legacy `side: 'mother' | 'father'` discriminator was removed because the
  // save handler derives the parent side from `updated.relationship` and any
  // other consumer can call `parentSideForComparator(state.comparator)`.
  const [comparatorEditState, setComparatorEditState] = useState<{
    comparator: Comparator
  } | null>(null)
  /**
   * GEN-3152 Phase 2: add/edit a non-parent family member (e.g. a legal
   * guardian who consents but is not sequenced). `comparator === null` opens
   * the blank add flow. Separate from `comparatorEditState` (mother/father
   * pencils) so the parent flow — with its legacy-slot / testOptions sync — is
   * untouched. Save only dispatches `upsertComparator`; the picker is
   * restricted to non-parent relationships, so the legacy parent slots are
   * never affected.
   */
  const [memberEditState, setMemberEditState] = useState<{
    comparator: Comparator | null
  } | null>(null)
  // R5 (side-nav comparator detail) state lives in the shared
  // `useOrderSideNavController` hook below — see that call for
  // `selectedComparatorId`, `addComparatorOpen`, and `comparatorSectionEdit`.
  const appStore = useStore<RootState>()
  const [clinicalModalOpenings, setClinicalModalOpenings] = useState(0)
  const latestClinicalModalOpenings = useRef(clinicalModalOpenings)
  latestClinicalModalOpenings.current = clinicalModalOpenings
  const clinicalModalOpenRef = useRef(false)
  const phenotypesAtClinicalModalOpen = useRef<{ clinicalDiagnosis: unknown; review: unknown } | null>(null)
  const setClinicalModalOpen = useCallback(
    (open: boolean) => {
      if (open && !clinicalModalOpenRef.current) {
        const reason = appStore.getState().canonicalOrder.canonicalOrder?.reason
        phenotypesAtClinicalModalOpen.current = {
          clinicalDiagnosis: reason?.clinicalDiagnosis,
          review: reason?.clinicalSummaryReview,
        }
        setClinicalModalOpenings((openings) => openings + 1)
      }
      clinicalModalOpenRef.current = open
      setClinicalModalOpenState(open)
    },
    [appStore]
  )
  /**
   * F.1c (GEN-2999) Stage 2: opens the canonical <ComparatorModal> for a
   * parent side. Replaces the legacy <BiologicalMother/FatherModal> openers
   * (validation fix-me links, guardian-modal "edit parent" callbacks). Plan C
   * (C-read-2): resolve the parent through the read adapter, which finds the
   * comparator (by side — relies on the MOTHER / FATHER 1-of-1 invariant) and
   * enriches Sample Type / consent / demographicId from the legacy slot at the
   * adapter boundary. Falls back to a bare comparator when neither a comparator
   * nor a slot exists yet (brand-new parent).
   */
  const openComparatorEditorForParent = useCallback(
    (side: 'mother' | 'father') => {
      const co = appStore.getState().canonicalOrder.canonicalOrder
      const relationship = side === 'mother' ? 'MOTHER' : 'FATHER'
      const comparator: Comparator = getParentComparator(co, side) ?? { relationship }
      setComparatorEditState({ comparator })
    },
    [appStore]
  )
  const [guardianContactModalOpen, setGuardianContactModalOpen] = useState(false)
  // GEN-5654: the Guardian Contact modal was opened by a comparator editor because
  // the platform collects consent to test and the order has no guardian contact.
  const [guardianContactRequiredForComparator, setGuardianContactRequiredForComparator] = useState(false)
  // GEN-5654: nothing on this page is "added after submission", so only flow B
  // (PRIMARY) makes the platform collect consent to test here; A and AB stay optional.
  const comparatorPlatformCollects = resolveComparatorPlatformCollects(effectiveConsentFlowForSubmit, false)
  const orderGuardianContactOnFile = orderHasGuardianContact(storedCanonical)
  const requestGuardianContactForComparator = useCallback(() => {
    setGuardianContactRequiredForComparator(true)
    setGuardianContactModalOpen(true)
  }, [])
  const comparatorGuardianProps = {
    platformCollectsConsentToTest: comparatorPlatformCollects,
    guardianContactOnFile: orderGuardianContactOnFile,
    onRequestGuardianContact: requestGuardianContactForComparator,
    coveredByGuardianModal: guardianContactModalOpen,
  }
  // True while the initial Epic FHIR guardian-contact prefetch is in flight.
  // Blocks editing the Guardian Contact section until it resolves so a provider
  // edit can't race the async result (avoids the stale-write clobber).
  const [guardianPrefillLoading, setGuardianPrefillLoading] = useState(false)
  const [patientDemographicsEditOpen, setPatientDemographicsEditOpen] = useState(false)
  // GEN-5738: the ordering-site selection must survive the pre-session window.
  // Before the draft exists, Redux `canonicalOrder` is null so `setOrderingSite`
  // no-ops; this local state holds the pick so it can gate + seed the create
  // POST, and is dispatched into Redux once the session exists. Seeded from the
  // New Order modal selection passed through navigation state (standalone create).
  const [pendingOrderingSite, setPendingOrderingSite] = useState(
    typeof locationState === 'object' && locationState !== null && locationState.orderingSite
      ? locationState.orderingSite
      : ''
  )
  const hasConfiguredDefaultTestType = useMemo(
    () => config.testTypes.some((testType) => testType.isDefault),
    [config.testTypes]
  )
  const defaultTestType = getDefaultTestType(config)
  // Prefer existing order's test type when form is already in Redux (avoids flashing singleton then Duo and extra getTestTypeConfig(singleton) call)
  const [testOptions, setTestOptions] = useState<TestOptions>(() => {
    const testType = hasConfiguredDefaultTestType ? defaultTestType : ''
    return {
      testType,
      otherFamilyMember: false,
      sampleType: initialProbandSampleTypeForOrderedTest(testType, config),
      selectedComparatorRelationships: initialComparatorRelationshipsForOrderedTest(testType, config),
    }
  })

  // Always-current mirror of `testOptions` so the async session-create effect
  // can reconcile the freshest test type / sample type after it seeds Redux.
  // The create POST is an awaited round-trip during which `storedCanonical` is
  // still null, so a fast pick in the Test Options modal dispatches
  // `mergePatient`/`mergeOrderSelection` that silently no-op (they bail on a
  // null canonical). The create effect closes over the `testOptions` captured
  // at mount and its deps exclude `testOptions`, so that pick is otherwise lost
  // — cleared silently and absent on refresh. This ref lets us replay it.
  const testOptionsRef = useRef(testOptions)
  testOptionsRef.current = testOptions
  // Always-current view of the Redux canonical, so the deferred-create closure
  // (Epic launch) persists the provider's post-launch edits rather than the
  // stale launch snapshot captured when the create effect started.
  const storedCanonicalRef = useRef(storedCanonical)
  storedCanonicalRef.current = storedCanonical
  // Always-current mirror of `sessionId` for the create closures/guards, whose
  // captured `sessionId` React state would otherwise be stale.
  const sessionIdRef = useRef(sessionId)
  sessionIdRef.current = sessionId

  useEffect(() => {
    traceOrderStart('snapshot', {
      sessionId,
      sessionHydrationDone,
      isHydrated,
      reduxOrderSelectedOption: storedCanonical?.order?.selectedOption ?? null,
      reduxOrderType: storedCanonical?.orderType ?? null,
      localTestType: testOptions.testType || '(empty)',
      hasOrderFormInfoData: Boolean(orderFormInfoData),
      sessionLoadedTestTypeRef: sessionLoadedTestTypeRef.current,
    })
  }, [
    sessionId,
    sessionHydrationDone,
    isHydrated,
    storedCanonical?.order?.selectedOption,
    storedCanonical?.orderType,
    testOptions.testType,
    orderFormInfoData,
  ])

  const [clinicalSummary, setClinicalSummary] = useState<ClinicalSummaryData>({
    summary: '',
    differentialDiagnosis: '',
    priorGeneticTesting: '',
  })

  const [indications, setIndications] = useState({
    phenotypes: [] as HPOTerm[],
  })
  const { resetIndicationLabelResolution } = useResolveIndicationHpoLabels(indications, setIndications)
  resetIndicationLabelResolutionRef.current = resetIndicationLabelResolution

  const [familyHistorySummary, setFamilyHistorySummary] = useState({
    includeFamilyHistory: true,
    summary: '',
    hasInteracted: false,
    familyMembersWithPhenotypes: {
      parent: false,
      grandparent: false,
      sibling: false,
      cousin: false,
    },
  })

  // F.1c Phase 4: the biological-parent UI state is no longer a separate React
  // useState hydrated imperatively from canonical — it is derived from the
  // canonical order + current TestOptions on every render. `comparators[]` /
  // the legacy mother/father slots are the single source of truth; every former
  // `setBiologicalMother/Father` site already dispatched the corresponding
  // canonical update, so this derivation reproduces the prior hydrated values
  // (behavior-preserving — see `deriveBiologicalParentsFromCanonical`).
  const { biologicalMother, biologicalFather } = useMemo(
    () => deriveBiologicalParentsFromCanonical(storedCanonical, testOptions, config),
    [storedCanonical, testOptions, config]
  )

  // Parent selection lives in `selectedComparatorRelationships`. P4: read
  // each side directly through the relationship accessor instead of
  // destructuring booleans, so the boolean-pair contract isn't a lock-in.
  const motherSelected = selectionHasRelationship(testOptions, 'MOTHER')
  const fatherSelected = selectionHasRelationship(testOptions, 'FATHER')

  // Phase C (MOTHER/FATHER migration): the FCC canonical predicates take the
  // selected-comparator relationship list instead of the retired
  // biologicalMother/Father boolean pair. Derive it from the same selection
  // those booleans came from so behavior is unchanged.
  const selectedParentRelationships = useMemo(
    () => [
      ...(motherSelected ? (['MOTHER'] as const) : []),
      ...(fatherSelected ? (['FATHER'] as const) : []),
    ],
    [motherSelected, fatherSelected]
  )

  const familySamplesCount = useMemo(
    () => [motherSelected, fatherSelected, testOptions.otherFamilyMember].filter(Boolean).length,
    [motherSelected, fatherSelected, testOptions.otherFamilyMember]
  )

  const orderSteps = useConfiguredOrderSteps({ currentStepIndex: 0 })

  const familySamplesDisplay = () => {
    const members = []
    if (motherSelected) members.push('Biological Mother')
    if (fatherSelected) members.push('Biological Father')
    if (testOptions.otherFamilyMember) members.push('Other Family Member')
    return members.join(' + ')
  }

  const familyHistoryIncluded = familyHistorySummary.includeFamilyHistory
  const familyHistoryTouched = Boolean(familyHistorySummary.hasInteracted)
  const hasFamilyHistorySummary = familyHistorySummary.summary.trim().length > 0

  const syncGuardianFromPrimaryParent = useCallback(
    (member: FamilyMember, relationshipToPatient: 'MOTHER' | 'FATHER') => {
      if (!member.testingRequested || !member.isPrimaryGuardian) return
      const fn = member.info.firstName?.trim() ?? ''
      const ln = member.info.lastName?.trim() ?? ''
      const em = member.info.email?.trim() ?? ''
      const ph = member.info.phone?.trim() ?? ''
      if (!fn && !ln && !em && !ph) return
      dispatch(
        setGuardianContact({
          contactNotAvailable: false,
          firstName: fn || null,
          lastName: ln || null,
          email: em || null,
          phone: ph || null,
          relationshipToPatient,
        })
      )
    },
    [dispatch]
  )

  const hasSelectedTestType = Boolean(testOptions.testType)
  const hasClinicalSummary = clinicalSummary.summary.trim().length > 0
  const hasIndications = indications.phenotypes.length > 0
  const selectedTestTypeConfig = useMemo(
    () => (testOptions.testType ? getTestTypeConfig(testOptions.testType, config) : undefined),
    [config, testOptions.testType]
  )
  const selectedTestTypeLabel =
    selectedTestTypeConfig?.label ?? selectedTestTypeConfig?.category ?? 'Ultra-Rapid Whole Genome Sequencing (urWGS)'
  const turnaroundDays = selectedTestTypeConfig?.turnaroundTimeInDays
  const turnaroundSequencingSubtitle =
    turnaroundDays != null
      ? formatTurnaroundTimeSequencingCardSubtitle(
          turnaroundDays,
          getTurnaroundTimeDayBasisForTestType(selectedTestTypeConfig)
        )
      : 'None entered'
  // R5 "Order Start (side nav)" / Sequencing Options decoupling — opt-in via
  // ``featureFlags.orderSideNav`` (DEV preview override ``?sidenav=1``). Hoisted
  // here because the parent-card gating below depends on it. Production gating
  // stays solely on the feature flag.
  const sideNavEnabled = isOrderSideNavEnabled(
    config.featureFlags?.orderSideNav,
    searchParams.get('sidenav')
  )
  // Collection-kit ordering — opt-in via ``featureFlags.kitOrdering``. Gates the
  // comparator "Request collection kit" toggle + shipment-address sub-form.
  const kitOrderingEnabled = isKitOrderingEnabled(config.featureFlags?.kitOrdering)
  // GEN-5738: ordering-site tracking (e.g. CHCM). When enabled, a REQUIRED site
  // dropdown gates draft creation and submit; the chosen name is written to
  // `canonicalOrder.orderingSite` and enforced/allowlist-validated by provider-api.
  const orderSiteTrackingEnabled = isOrderSiteTrackingEnabled(config)
  const organizationSiteNames = useMemo(() => getOrganizationSiteNames(config), [config])
  // Prefer the persisted Redux value (post-session) but fall back to the local
  // pending pick so the selection is honored before the draft exists.
  const selectedOrderingSite = storedCanonical?.orderingSite ?? pendingOrderingSite
  // A valid selection = a non-blank site that is still in the pinned config's
  // allowlist (guards the stale-config case client-side, before the 422).
  const orderingSiteSelectionValid =
    !orderSiteTrackingEnabled || isValidOrganizationSite(selectedOrderingSite, config)
  // Always-current mirrors for the create closures. `performCreate` (Epic
  // deferred path) is captured into `createDeferredSessionRef` on the run that
  // armed it and is NOT recaptured while the create stays armed — so reading the
  // render-time `orderingSiteSelectionValid` / `selectedOrderingSite` would let a
  // stale (site-not-yet-picked) value gate the POST forever even after a valid
  // site is chosen. Reading these refs keeps the site gate honest.
  const orderingSiteSelectionValidRef = useRef(orderingSiteSelectionValid)
  orderingSiteSelectionValidRef.current = orderingSiteSelectionValid
  const selectedOrderingSiteRef = useRef(selectedOrderingSite)
  selectedOrderingSiteRef.current = selectedOrderingSite
  // R5 decoupling (Phase 2): parent "requestedness" is derived from the
  // selected comparators (`motherSelected` / `fatherSelected`, which themselves
  // derive from `comparators[]` when present) rather than from the test type's
  // `parentSamplesRequired` tier integer. The test-type tier is retained only
  // as the *maximum* parent comparators the product line supports, so the
  // family section / sequencing card can still be offered for tiered test
  // types before any parent is added. `requestedParentCount` (0/1/2) replaces
  // the tier integer everywhere "how many parents are requested" mattered, so
  // validation, copy, and card gating follow the comparators the user added.
  //
  // R5 sequencing decoupling (config_max): the modal resolves a category to its
  // proband-only SKU, so the gating below must use the *category's* max parent
  // capability (across its proband/duo/trio SKUs) rather than the resolved SKU's
  // own tier — otherwise the "add a parent" entry point would never appear on a
  // proband-resolved category. Legacy layout keeps the resolved SKU's own tier.
  const maxParentSamplesForTestType = sideNavEnabled
    ? getMaxParentSamplesForCategory(selectedTestTypeConfig?.category, config)
    : (selectedTestTypeConfig?.parentSamplesRequired ?? 0)
  const requestedParentCount = (motherSelected ? 1 : 0) + (fatherSelected ? 1 : 0)
  // R5 (Rady SKU mapping): the Duo/Trio tier follows the number of comparators
  // being sequenced alongside the proband, regardless of relationship — proband
  // + 1 tested comparator (parent, grandparent, sibling, …) bills the Duo SKU,
  // proband + 2 tested comparators bills the Trio SKU, capped at the category
  // max (2). Proband-only = 0 tested comparators. Non-testing relatives (a
  // guardian who consents but isn't sequenced) and the proband row itself don't
  // add a sample. orderType (family structure) is computed separately from the
  // biological-parent flags and is intentionally unaffected by this tier.
  const testingComparatorCount = (storedCanonical?.comparators ?? []).filter(
    (c) => c != null && c.isTestingComparator !== false && c.relationship !== 'PROBAND'
  ).length
  const duoComparatorUnset =
    maxParentSamplesForTestType === 1 && !motherSelected && !fatherSelected
  const parentSampleTypes = selectedTestTypeConfig?.parentSampleTypes
  // Show the family section when any parent comparator is selected OR the test
  // type supports parent samples (so the user has an entry point to add one).
  const showParentCards = maxParentSamplesForTestType > 0 || motherSelected || fatherSelected
  // R5: Guardian Contact is a patient-level field (the guardian who receives
  // results / consents on the patient's behalf), so it belongs under "Patient"
  // in the side-nav flow even when biological-parent comparators are shown. The
  // legacy single-column layout keeps the original proband-only gating.
  const showGuardianContact = sideNavEnabled || !showParentCards

  const comparatorParentConsentConfigs = useMemo(
    () => getPatientConsents(config),
    [config]
  )

  useEnforceNonApplicableSecondaryConsentDefaults(comparatorParentConsentConfigs, storedCanonical, dispatch)

  // R5 sequencing decoupling (config_max): keep the submitted SKU
  // (`canonical.order.selectedOption`) and the local test-type form state
  // aligned with the comparator-derived tier. The Sequencing Options modal picks
  // a *speed* (category → proband SKU); as comparators are added or removed, the
  // order must bill the matching duo/trio SKU for that category.
  //
  // Rady mapping: the tier is the total number of testing comparators (any
  // relationship), capped at the category's max available tier — so category +
  // comparator count resolves the specific selected test referenced in the
  // create-order payload. This is the single sync point covering every comparator
  // mutation path. No-op when the category lacks a SKU at the resolved tier
  // (keeps the current selection rather than mis-billing).
  useEffect(() => {
    if (!sideNavEnabled) return
    const current = storedCanonical?.order?.selectedOption
    const category = current ? getTestTypeConfig(current as TestType, config)?.category : undefined
    if (!category) return
    const maxTier = getMaxParentSamplesForCategory(category, config)
    const tier = Math.min(testingComparatorCount, maxTier)
    const desired = resolveTestTypeForCategoryTier(category, tier, config)
    if (!desired || desired === current) return
    dispatch(mergeOrderSelection({ selectedOption: desired, labName: getLabName(config) || null }))
    setTestOptions((prev) => (prev.testType === desired ? prev : { ...prev, testType: desired }))
  }, [sideNavEnabled, storedCanonical?.order?.selectedOption, testingComparatorCount, config, dispatch])

  /**
   * R5 (draft) Add-Comparator persistence. In the side-nav flow a parent is
   * just another comparator: the mother/father-specific SKU engine
   * (`applySequencingOptionsChangeToCanonical`) — with its empty-parent seeding,
   * duo primary-guardian defaulting, and legacy-slot promotion — must NOT run,
   * because that seeding both clobbers the user-authored row and nulls a
   * standalone Guardian Contact card (the reducer's single-primary-guardian
   * invariant). Instead we upsert the authored comparator (source of truth),
   * keep the local relationship selection in sync, and set `orderType` straight
   * from `comparators[]`. The SKU tier follows automatically via the
   * comparator-count sync effect above. Non-parent rows just upsert.
   */
  const handleAddComparatorPersist = useCallback(
    (withId: Comparator) => {
      const side = parentSideForComparator(withId)
      dispatch(upsertComparator(withId))
      if (side) {
        const nextSelection = withRelationshipSelection(
          testOptions,
          side === 'mother' ? 'MOTHER' : 'FATHER',
          true
        )
        setTestOptions(nextSelection)
        // Derive orderType from the resulting comparator set (the upsert above
        // isn't reflected in `storedCanonical` synchronously, so project the
        // added side onto the current comparators).
        const existing = (storedCanonical?.comparators ?? []).filter(
          (c): c is Comparator => c != null && parentSideForComparator(c) !== side
        )
        const projected = {
          ...(storedCanonical ?? {}),
          comparators: [...existing, withId],
        } as CanonicalOrderPayload
        dispatch(setOrderType(orderTypeFromComparators(projected)))
      }
    },
    [dispatch, storedCanonical, testOptions]
  )

  const {
    selectedComparatorId,
    setSelectedComparatorId,
    resolveComparatorById,
    sideNavSections,
    comparatorGroups,
    comparatorLimitReached,
    comparatorLimitReachedReason,
    primaryGuardianComparator,
    primaryGuardianComparatorComplete,
    addComparatorRelationshipOptions,
    comparatorSectionEdit,
    setComparatorSectionEdit,
    addComparatorOpen,
    setAddComparatorOpen,
    patientConsentModalOpen,
    setPatientConsentModalOpen,
    handleComparatorSectionSave,
    handleAddComparatorSave,
  } = useOrderSideNavController({
    sideNavEnabled,
    storedCanonical,
    comparatorParentConsentConfigs,
    requireClinicalSummary: Boolean(config.validationRules?.requireClinicalSummary),
    requirePhenotypes: Boolean(config.validationRules?.requirePhenotypes),
    familyHistoryRequired: Boolean(config.validationRules?.requireFamilyHistory),
    showGuardianContact,
    onAddComparatorSave: handleAddComparatorPersist,
    fulfillmentStrategy: getFulfillmentStrategy(config),
    config,
    selectedCategory: testOptions.testType ? getTestTypeConfig(testOptions.testType, config)?.category : undefined,
  })

  // Publish the (draft flow has no sticky header) sticky-top offset so the
  // stacked layout pins the left nav + per-person headers consistently.
  useOrderStickyTop(sideNavEnabled)

  // Lifted narrow-screen (`< lg`) side-nav drawer visibility so the "Open order
  // navigation" burger can render inline in the header (next to the patient
  // name) while the drawer + backdrop stay inside OrderSideNav.
  const [sideNavDrawerOpen, setSideNavDrawerOpen] = useState(false)

  // Display name for the stacked-layout "Patient" group header (draft flow has
  // no precomputed patient-name variable). Falls back to an unnamed header.
  const orderStartPatientName = [
    storedCanonical?.patient?.firstName,
    storedCanonical?.patient?.lastName,
  ]
    .filter((p) => p && String(p).trim())
    .join(' ')
    .trim()

  /**
   * Ordered list of the patient section modals the user can step through via
   * each card's "Continue to …" CTA. Mirrors the rendered card order and skips
   * sections that aren't editable in the current layout (e.g. Guardian Contact
   * when it mirrors a primary-guardian comparator, or Consent outside the R5
   * side-nav layout where it renders inline rather than as a modal). Each card's
   * continue button targets the next entry, so hidden sections never break the
   * chain.
   */
  const sectionModalSteps = useMemo<
    Array<{ id: 'sequencing' | 'clinical' | 'family-history' | 'guardian' | 'consent'; label: string; open: () => void }>
  >(() => {
    const steps: Array<{
      id: 'sequencing' | 'clinical' | 'family-history' | 'guardian' | 'consent'
      label: string
      open: () => void
    }> = [
      { id: 'sequencing', label: 'Sequencing Options', open: () => setTestOptionsModalOpen(true) },
      { id: 'clinical', label: 'Clinical Summary', open: () => setClinicalModalOpen(true) },
      { id: 'family-history', label: 'Family History', open: () => setFamilyHistorySummaryModalOpen(true) },
    ]
    // Consent ("Additional Options") precedes Guardian Contact: consent choices
    // (research-contact opt-in / incidental findings) gate whether Guardian
    // Contact is required, so the "Continue to …" chain visits consent first.
    if (sideNavEnabled && storedCanonical && comparatorConsentAnyApplicable(comparatorParentConsentConfigs)) {
      steps.push({ id: 'consent', label: 'Additional Options', open: () => setPatientConsentModalOpen(true) })
    }
    if (showGuardianContact && !primaryGuardianComparator) {
      steps.push({ id: 'guardian', label: 'Guardian Contact', open: () => setGuardianContactModalOpen(true) })
    }
    return steps
  }, [
    showGuardianContact,
    primaryGuardianComparator,
    sideNavEnabled,
    storedCanonical,
    comparatorParentConsentConfigs,
    setTestOptionsModalOpen,
    setClinicalModalOpen,
    setFamilyHistorySummaryModalOpen,
    setGuardianContactModalOpen,
    setPatientConsentModalOpen,
  ])

  /** The next visible section modal after `currentId`, or `null` if last. */
  const getNextSectionStep = useCallback(
    (currentId: (typeof sectionModalSteps)[number]['id']) => {
      const idx = sectionModalSteps.findIndex((s) => s.id === currentId)
      if (idx === -1) return null
      return sectionModalSteps[idx + 1] ?? null
    },
    [sectionModalSteps]
  )

  // Incidental is gated for completeness/validation only when applicable AND
  // authored `requireInteraction: true` (consistent with NY / research).
  const incidentalConsentApplicable = incidentalConsentGated(comparatorParentConsentConfigs)
  // NY sample-retention / research-contact submit gates: engaged only when the
  // section is applicable AND authored `requireInteraction: true`.
  const consentInteractionGates = useMemo(
    () => resolveConsentInteractionGates(comparatorParentConsentConfigs),
    [comparatorParentConsentConfigs]
  )

  // MOTHER/FATHER migration #6: the legacy single-column banner's per-parent
  // "needs your input" links are derived by iterating the selected parent
  // relationships instead of two hardcoded mother/father memos. Each row
  // reproduces the prior completeness chain (biological-parent complete +
  // proband research-contact channel obligation + incidental opt-in/out) so the
  // legacy banner output is unchanged. The R5 side-nav banner already iterates
  // `comparators[]` directly (see the `sideNavEnabled` branch below).
  // C1.3: each row resolves a comparator-shaped view via `readParent`,
  // preferring `comparators[]` and falling back to a legacy-slot synthesis, so
  // badge/section-complete predicates see consent / specimen / clinical metadata
  // that lives on the comparator (but not the legacy slot) for old draft payloads.
  const parentAttentionRows = useMemo(
    () =>
      selectedParentRelationships.map((relationship) => {
        const side: 'mother' | 'father' = relationship === 'MOTHER' ? 'mother' : 'father'
        const comparator = FormCompletionChecker.readParent(storedCanonical, side)
        const complete = FormCompletionChecker.isBiologicalParentComplete(
          FormCompletionChecker.comparatorAsParentData(comparator)
        )
        const needsResearchContactChannel = storedCanonical
          ? FormCompletionChecker.canonicalParentNeedsResearchContactChannelPrompt(side, storedCanonical, {
              parentSamplesRequired: requestedParentCount,
              selectedComparatorRelationships: selectedParentRelationships,
            })
          : false
        const incidentalIncomplete = !FormCompletionChecker.canonicalComparatorIncidentalChoiceComplete(comparator, {
          incidentalApplicable: incidentalConsentApplicable,
          slotActive: true,
        })
        // NY sample-retention / research-contact consents block submit when their
        // `requireInteraction` gate is engaged (see the legacy canSubmit path),
        // so an unanswered parent consent must also surface a fix-me link here.
        const secondaryConsentIncomplete =
          comparator != null &&
          !FormCompletionChecker.comparatorConsentComplete(comparator, {
            nyRetentionRequireInteraction: consentInteractionGates.nyRetentionRequireInteraction,
            researchContactRequireInteraction: consentInteractionGates.researchContactRequireInteraction,
          })
        return {
          relationship,
          side,
          anchorId: parentSectionAnchorIdForRelationship(relationship),
          label: parentSectionLabelForRelationship(relationship),
          needsAttention: !complete || needsResearchContactChannel || incidentalIncomplete || secondaryConsentIncomplete,
        }
      }),
    [
      selectedParentRelationships,
      storedCanonical,
      requestedParentCount,
      incidentalConsentApplicable,
      consentInteractionGates,
    ]
  )

  /** Proband Incidental Findings is required (org-applicable) and not yet opted in/out. */
  const probandIncidentalIncomplete = useMemo(() => {
    if (!incidentalConsentApplicable) return false
    const sel = storedCanonical?.patient?.incidentalFindingsSelection
    return sel !== 'opt-in' && sel !== 'opt-out'
  }, [incidentalConsentApplicable, storedCanonical?.patient?.incidentalFindingsSelection])

  /**
   * Proband NY sample-retention / research-contact are unanswered while their
   * `requireInteraction` gate is engaged. Drives the "Additional Options"
   * needs-attention link alongside {@link probandIncidentalIncomplete}.
   */
  const probandSecondaryConsentIncomplete = useMemo(() => {
    const nyIncomplete =
      consentInteractionGates.nyRetentionRequireInteraction &&
      storedCanonical?.patient?.newYorkSampleRetentionOptIn !== true &&
      storedCanonical?.patient?.newYorkSampleRetentionOptIn !== false
    const researchIncomplete =
      consentInteractionGates.researchContactRequireInteraction &&
      storedCanonical?.patient?.researchContactConsent !== true &&
      storedCanonical?.patient?.researchContactConsent !== false
    return Boolean(nyIncomplete || researchIncomplete)
  }, [
    consentInteractionGates,
    storedCanonical?.patient?.newYorkSampleRetentionOptIn,
    storedCanonical?.patient?.researchContactConsent,
  ])

  /** Any proband consent ("Additional Options") requirement is outstanding. */
  const probandConsentNeedsAttention = probandIncidentalIncomplete || probandSecondaryConsentIncomplete

  const scrollToProbandIncidentalFindings = useCallback(() => {
    const el = document.getElementById(PROBAND_INCIDENTAL_FINDINGS_ANCHOR_ID)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      // UX-023/UX-024: pulse the inline Incidental Findings section on arrival.
      pulseElementById(PROBAND_INCIDENTAL_FINDINGS_ANCHOR_ID)
    }
  }, [])

  /**
   * UX-023/UX-024: apply a transient "you were sent here" pulse to a section
   * anchor. Re-triggers cleanly if the same target is pulsed again (the class is
   * removed, layout is flushed via reflow, then re-added), and is torn down
   * after the animation so it doesn't linger. No-op when the element is absent.
   */
  const pulseSection = useCallback((anchorId: string) => {
    pulseElementById(anchorId)
  }, [])

  /**
   * UX-023: banner deeplink for a patient section that has an editor modal —
   * open the modal (parity with the legacy single-column banner) and pulse the
   * underlying section card as the "you were sent here" confirmation. Clears any
   * open comparator detail view first so the patient column (and its anchor) is
   * mounted before we pulse it.
   */
  const openSectionEditor = useCallback(
    (anchorId: string, openModal: () => void) => {
      setSelectedComparatorId(null)
      window.requestAnimationFrame(() => {
        pulseSection(anchorId)
        openModal()
      })
    },
    [setSelectedComparatorId, pulseSection]
  )

  /**
   * UX-023/UX-024: banner deeplink for an inline section (no modal editor, e.g.
   * proband Incidental Findings / Consent) — scroll to it and pulse it.
   */
  const scrollAndPulseSection = useCallback(
    (anchorId: string) => {
      setSelectedComparatorId(null)
      window.requestAnimationFrame(() => {
        document.getElementById(anchorId)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        pulseSection(anchorId)
      })
    },
    [setSelectedComparatorId, pulseSection]
  )

  const clearFatherPrimaryForTrio = () => {
    if (requestedParentCount !== 2) return
    const f = getParentComparator(storedCanonical, 'father')
    if (f?.isPrimaryGuardian === true) {
      dispatch(upsertComparator({ ...f, isPrimaryGuardian: false }))
    }
  }
  const clearMotherPrimaryForTrio = () => {
    if (requestedParentCount !== 2) return
    const m = getParentComparator(storedCanonical, 'mother')
    if (m?.isPrimaryGuardian === true) {
      dispatch(upsertComparator({ ...m, isPrimaryGuardian: false }))
    }
  }

  const familySamplesTitle = showParentCards
    ? duoComparatorUnset
      ? 'Comparator sample (required)'
      : `Include ${familySamplesCount} family sample${familySamplesCount !== 1 ? 's' : ''} for comparison`
    : 'No comparator samples'
  const familySamplesDescription = showParentCards
    ? duoComparatorUnset
      ? 'Choose Biological Mother or Biological Father in sequencing options.'
      : familySamplesDisplay() || 'No family samples selected'
    : 'Family members won’t be tested'
  const familyHistoryDetails = familyHistoryIncluded
    ? formatFamilyHistoryDetails(familyHistorySummary.familyMembersWithPhenotypes)
    : undefined
  const familyHistoryRequired = Boolean(config.validationRules?.requireFamilyHistory)
  const familyHistoryStatusMessage = !familyHistoryIncluded
    ? 'Not included'
    : hasFamilyHistorySummary
      ? 'Complete'
      : 'Family history needed for ordering'
  const familyHistoryNeedsAttention = familyHistoryRequired && familyHistoryIncluded && !hasFamilyHistorySummary
  // Green success is reserved for actually-completed / not-included family
  // history. An empty *optional* section ("Optional") is neutral
  // gray, not green — a green pill wrongly implied it was done (UX-015).
  const familyHistoryStatusVariant: 'success' | 'info' | 'neutral' = hasFamilyHistorySummary
    ? 'success'
    : !familyHistoryIncluded
      ? 'success'
      : !familyHistoryRequired
        ? 'neutral'
        : 'info'
  const familyHistoryDisplayMessage = familyHistoryRequired
    ? familyHistoryStatusMessage
    : hasFamilyHistorySummary
      ? 'Complete'
      : 'Optional'

  const guardianContactNeedsAttentionForResearch = useMemo(() => {
    // Legacy single-column layout keeps the original proband-only gating (the
    // guardian card only appears when there are no parent cards). The R5
    // side-nav surfaces guardian contact as a patient-level field on every
    // order, so the requirement applies regardless of family structure: when
    // the proband opts into research contact (or the flow is PRIMARY), guardian
    // contact is not optional — satisfied by the legacy slot OR a primary-
    // guardian comparator (mirrors the submit gate's shared predicate).
    if (!sideNavEnabled && showParentCards) return false
    const probandResearchOptIn = storedCanonical?.patient?.researchContactConsent === true
    const needsGuardian = probandResearchOptIn || effectiveConsentFlowForSubmit.trim() === 'PRIMARY'
    if (!needsGuardian) return false
    return !FormCompletionChecker.standaloneGuardianContactSatisfied(storedCanonical, {
      decoupledComparators: sideNavEnabled,
      requireContactChannel: probandResearchOptIn,
    })
  }, [
    sideNavEnabled,
    showParentCards,
    storedCanonical,
    effectiveConsentFlowForSubmit,
  ])

  const mrnCollision = useMemo(
    () => FormCompletionChecker.findCanonicalOrderMrnCollision(storedCanonical),
    [storedCanonical]
  )

  /**
   * The clinical modals block their own Done buttons, but autosave already
   * stored the text. Continue must stay disabled so an order over its lab's
   * clinical-text limits never reaches Submit.
   */
  const clinicalTextError = useMemo(
    () => clinicalTextLimitError(getFulfillmentStrategy(config), storedCanonical),
    [config, storedCanonical]
  )
  const clinicalCombinedOverLimit = clinicalTextError !== null

  // Needs-input review rows (terms no longer in the summary stay on the order
  // and do not block), or a summary that no longer matches the last merge
  // fingerprint (edit in flight before live extraction finishes).
  const clinicalSummaryReviewBlocksSubmit = blocksSubmit(
    storedCanonical?.reason?.clinicalSummaryReview,
    storedCanonical?.clinical?.clinicalNote ?? ''
  )

  const canSubmit = useMemo(() => {
    // GEN-5738: block submit when the org tracks ordering sites and the current
    // selection is missing or no longer in the pinned config's allowlist (a
    // stale pinned config). Mirrors the provider-api create/patch enforcement so
    // the header submit button disables before the server 422.
    if (!orderingSiteSelectionValid) return false
    if (clinicalCombinedOverLimit) return false
    if (clinicalSummaryReviewBlocksSubmit) return false
    // R5 (decoupled completion): the SKU tier follows the *number* of
    // comparators (any relationship), not specific biological mother/father
    // slots — so a trio can be Mother + Grandfather. Validate the shared
    // proband-level requirements with the parent-slot gating disabled
    // (`parentSamplesRequired: 0`, which also applies the patient-level guardian
    // rule that R5 relies on), then require every comparator row to be complete
    // on its own terms. This replaces the legacy "biological mother AND father"
    // gate that left such orders permanently unsubmittable.
    if (sideNavEnabled) {
      const incidentalApplicable = incidentalConsentGated(comparatorParentConsentConfigs)
      const base = FormCompletionChecker.isCanonicalOrderSubmittable(storedCanonical, {
        parentSamplesRequired: 0,
        selectedComparatorRelationships: [],
        decoupledComparators: true,
        requireClinicalSummary: Boolean(config.validationRules?.requireClinicalSummary),
        requirePhenotypes: Boolean(config.validationRules?.requirePhenotypes),
        requireFamilyHistory: Boolean(config.validationRules?.requireFamilyHistory),
        familyHistoryTouched,
        familyHistoryIncluded,
        incidentalFindingsApplicable: incidentalApplicable,
        nyRetentionRequireInteraction: consentInteractionGates.nyRetentionRequireInteraction,
        researchContactRequireInteraction: consentInteractionGates.researchContactRequireInteraction,
        effectiveConsentFlow: effectiveConsentFlowForSubmit,
      })
      if (!base) return false
      if (mrnCollision) return false
      return (storedCanonical?.comparators ?? [])
        .filter((c): c is Comparator => c != null && c.relationship !== 'PROBAND')
        .every((c) =>
          FormCompletionChecker.isCanonicalComparatorComplete(c, {
            incidentalApplicable,
            probandResearchContactConsent: storedCanonical?.patient?.researchContactConsent === true,
            nyRetentionRequireInteraction: consentInteractionGates.nyRetentionRequireInteraction,
            researchContactRequireInteraction: consentInteractionGates.researchContactRequireInteraction,
          })
        )
    }
    if (mrnCollision) return false
    const legacyBase = FormCompletionChecker.isCanonicalOrderSubmittable(storedCanonical, {
      parentSamplesRequired: requestedParentCount,
      selectedComparatorRelationships: selectedParentRelationships,
      requireClinicalSummary: Boolean(config.validationRules?.requireClinicalSummary),
      requirePhenotypes: Boolean(config.validationRules?.requirePhenotypes),
      requireFamilyHistory: Boolean(config.validationRules?.requireFamilyHistory),
      familyHistoryTouched,
      familyHistoryIncluded,
      incidentalFindingsApplicable: incidentalConsentGated(comparatorParentConsentConfigs),
      nyRetentionRequireInteraction: consentInteractionGates.nyRetentionRequireInteraction,
      researchContactRequireInteraction: consentInteractionGates.researchContactRequireInteraction,
      effectiveConsentFlow: effectiveConsentFlowForSubmit,
    })
    if (!legacyBase) return false
    // `isCanonicalOrderSubmittable` only gates the proband's NY / research /
    // incidental consents; it does not run comparator rows through the
    // `requireInteraction` gates. Validate the SAME rows the legacy layout
    // treats as part of the order — the selected biological parents, resolved
    // exactly as the incomplete-sections banner does (`parentAttentionRows`) —
    // rather than every row in `comparators[]`. Gating leftover / unselected
    // rows here would be stricter than both `isCanonicalIncidentalFindingsSubmittable`
    // (which skips incidental until a parent is fully complete) and the banner
    // (which only surfaces selected mother/father links), leaving submit blocked
    // with no matching fix-me link.
    return selectedParentRelationships.every((relationship) => {
      const side: 'mother' | 'father' = relationship === 'MOTHER' ? 'mother' : 'father'
      const comparator = FormCompletionChecker.readParent(storedCanonical, side)
      if (!comparator) return true
      return FormCompletionChecker.comparatorConsentComplete(comparator, {
        incidentalApplicable: incidentalConsentGated(comparatorParentConsentConfigs),
        nyRetentionRequireInteraction: consentInteractionGates.nyRetentionRequireInteraction,
        researchContactRequireInteraction: consentInteractionGates.researchContactRequireInteraction,
      })
    })
  }, [
    sideNavEnabled,
    storedCanonical,
    mrnCollision,
    config.validationRules,
    requestedParentCount,
    familyHistoryTouched,
    familyHistoryIncluded,
    selectedParentRelationships,
    effectiveConsentFlowForSubmit,
    comparatorParentConsentConfigs,
    consentInteractionGates,
    orderingSiteSelectionValid,
    clinicalCombinedOverLimit,
        clinicalSummaryReviewBlocksSubmit,
  ])

  // R5 side-nav controller members (sideNavSections, comparatorGroups,
  // primaryGuardianComparator, selectedComparator, showComparatorDetail,
  // comparatorDetailRef, the ensureComparatorIds backfill effect, the
  // drop-selection effect, the scroll-into-view effect,
  // handleAddComparatorSave, handleComparatorSectionSave, and
  // addComparatorRelationshipOptions) live in the shared
  // `useOrderSideNavController` hook called above.

  /** Same shape as the workspace “New Order” modal (`NewOrderModal`), for editing proband before submit. */
  const newOrderPatientEditInitial = useMemo((): OrderFormData | null => {
    const p = orderFormInfoData?.patient
    if (!p) return null
    let dateStr = ''
    if (p.birthDate) {
      const date = p.birthDate instanceof Date ? p.birthDate : new Date(p.birthDate)
      if (!isNaN(date.getTime())) {
        const y = date.getFullYear()
        const m = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        dateStr = `${y}-${m}-${day}`
      }
    }
    const orderingPhysician =
      `${auth0User?.given_name ?? ''} ${auth0User?.family_name ?? ''}`.trim() ||
      auth0User?.name ||
      auth0User?.email ||
      ''
    return {
      patientFirstName: p.name?.firstName ?? '',
      patientLastName: p.name?.lastName ?? '',
      dateOfBirth: dateStr,
      mrn: p.mrn ?? '',
      sex: p.sex ?? Sex.Unknown,
      orderingPhysician,
      orderingSite: selectedOrderingSite,
    }
  }, [orderFormInfoData, auth0User, selectedOrderingSite])

  // GEN-5738: apply an ordering-site pick. Writes local state (so it survives
  // the pre-session window where Redux `canonicalOrder` is null and dispatches
  // into Redux (a no-op pre-session, authoritative once the draft exists so the
  // value autosaves via the PATCH path). Clears any prior field error and
  // re-validates against the pinned config's allowlist.
  // GEN-5738: apply an ordering-site pick from the New Order modal. Writes local
  // state (so it survives the pre-session window where Redux `canonicalOrder` is
  // null) and dispatches into Redux (a no-op pre-session, authoritative once the
  // draft exists so the value autosaves via the PATCH path). The modal owns its
  // own field-level validation/error display; the page only surfaces submit-time
  // server errors via the existing `orderActionError` banner.
  const handleOrderingSiteChange = useCallback(
    (siteName: string) => {
      setPendingOrderingSite(siteName)
      if (storedCanonical) {
        dispatch(setOrderingSite(siteName || null))
      }
    },
    [dispatch, storedCanonical]
  )

  const handleNewOrderModalPatientSave = useCallback(
    (data: OrderFormData) => {
      if (!orderFormInfoData?.patient) return
      const existingMiddle = orderFormInfoData.patient.name?.middleName
      const nextPatient: OrderFormInfoData['patient'] = {
        ...orderFormInfoData.patient,
        name: {
          firstName: data.patientFirstName,
          lastName: data.patientLastName,
          ...(existingMiddle?.trim() ? { middleName: existingMiddle.trim() } : {}),
        },
        mrn: data.mrn,
        sex: data.sex,
        birthDate: data.dateOfBirth ? new Date(data.dateOfBirth) : null,
      }
      const nextOrderForm: OrderFormInfoData = {
        ...orderFormInfoData,
        patient: nextPatient,
      }
      setOrderFormInfoData(nextOrderForm)
      setPatientInfo(getPatientInfoFromForm(nextOrderForm))
      dispatch(
        mergePatient({
          firstName: data.patientFirstName,
          lastName: data.patientLastName,
          middleName: existingMiddle?.trim() || null,
          mrn: data.mrn,
          sex: String(data.sex),
          dateOfBirth: data.dateOfBirth,
        })
      )
      // GEN-5738: persist the chosen ordering site. `handleOrderingSiteChange`
      // updates the local pending value (so it survives the pre-session window
      // and gates the create POST) and dispatches into Redux once the draft
      // exists so it autosaves via PATCH.
      if (orderSiteTrackingEnabled && data.orderingSite !== undefined) {
        handleOrderingSiteChange(data.orderingSite)
      }
      setPatientDemographicsEditOpen(false)
    },
    [dispatch, orderFormInfoData, orderSiteTrackingEnabled, handleOrderingSiteChange]
  )

  const getCallerName = (data?: OrderFormInfoData | null) => {
    if (authFlow === 'epic' && state) {
      // Epic flow - use EpicAuthClient context
      const accessContext = EpicAuthClient.getInstance().getContext(state)
      const epicName = `${accessContext.userName || ''} ${accessContext.userLastName || ''}`.trim()
      if (epicName) {
        return epicName
      }
    } else {
      // Standalone flow - use Auth0 user info
      if (auth0User?.name) {
        return auth0User.name
      }
      if (auth0User?.email) {
        return auth0User.email
      }
    }
    // Fallback to provider name or default
    const providerName = data?.orderingProvider?.name
      ? `${data.orderingProvider.name.firstName || ''} ${data.orderingProvider.name.lastName || ''}`.trim()
      : ''
    return providerName || 'spa'
  }

  /**
   * For standalone flow (e.g. draft opened from workspace list), ensure orderingProvider.id
   * is set from Auth0 when missing so provider_id is sent on save/submit.
   */
  const getOrderFormInfoWithProviderId = (data: OrderFormInfoData): OrderFormInfoData => {
    if (authFlow !== 'standalone' || !auth0User) {
      return data
    }
    const providerId = auth0User.sub || auth0User.email
    if (!providerId || data.orderingProvider?.id) {
      return data
    }
    const firstName =
      data.orderingProvider?.name?.firstName ||
      data.medicalProfessionalInfo?.name?.split(/\s+/)[0] ||
      auth0User.given_name ||
      ''
    const lastName =
      data.orderingProvider?.name?.lastName ||
      data.medicalProfessionalInfo?.name?.split(/\s+/).slice(1).join(' ') ||
      auth0User.family_name ||
      ''
    return {
      ...data,
      orderingProvider: {
        ...data.orderingProvider,
        id: providerId,
        name: { firstName, lastName },
        roleTitle: data.orderingProvider?.roleTitle ?? '',
        phone: data.orderingProvider?.phone ?? '',
        email:
          data.orderingProvider?.email ??
          data.medicalProfessionalInfo?.email ??
          auth0User.email ??
          '',
        npi: data.orderingProvider?.npi ?? data.medicalProfessionalInfo?.npi ?? '',
      },
    }
  }

  const handleSubmitOrder = async () => {
    if (!orderFormInfoData) {
      setOrderActionError('Unable to submit order without patient data.')
      return
    }
    if (!canSubmit) {
      return
    }
    if (mergedDraftConflictReloadingRef.current) {
      setOrderActionError(CONFLICT_RELOAD_FAILED_MESSAGE)
      return
    }
    setOrderActionError(null)
    if (isSubmittingForReview) {
      return
    }

    setIsSubmittingForReview(true)
    const dataToSubmit = getOrderFormInfoWithProviderId(orderFormInfoData)
    await Sentry.startSpan({ op: 'ui.click', name: 'Submit Order Review' }, async (span) => {
      try {
        // Epic launches defer session creation until the first edit; advancing to
        // review is itself a create trigger. If no session exists yet, force the
        // deferred create now so the review page gets a real session (needed for
        // the session-based submit + Redox/coverage path). `performCreate` is
        // idempotent (guarded by createInFlightRef / sessionIdRef), so calling it
        // here is safe even if an edit already kicked off (or completed) a create.
        let effectiveSessionId = sessionId ?? sessionIdRef.current
        let createdHere = false
        if (!effectiveSessionId && createDeferredSessionRef.current) {
          pendingCreateArmedRef.current = false
          effectiveSessionId = await createDeferredSessionRef.current()
          createdHere = Boolean(effectiveSessionId)
        }
        if (!effectiveSessionId) {
          setOrderActionError('Session is still initializing, please wait a moment.')
          return
        }
        // GEN-5738: last-line client guard before the pre-review PATCH. `canSubmit`
        // already gates the button, but re-check here so a stale allowlist / missing
        // site surfaces as a visible banner instead of a silent no-op.
        if (orderSiteTrackingEnabled && !isValidOrganizationSite(selectedOrderingSite, config)) {
          setOrderActionError(
            selectedOrderingSite
              ? 'Selected ordering site is not valid for this organization.'
              : 'An ordering site is required for this organization.'
          )
          return
        }
        const providerClient = ProviderAPIClient.getInstance()
        // Skip the pre-review PATCH when `performCreate` just created the session in
        // this handler: it already persisted the live canonical (including any
        // persist-time Epic guardian merge and post-POST flush). PATCHing again here
        // would use `storedCanonical` — the click-time render snapshot, which lags
        // both the guardian sync and the flush — and overwrite the fresh session.
        if (!createdHere) {
          // Prefer the live store ref (reflects the latest committed edits) over the
          // closed-over render snapshot; fall back to building canonical from form
          // context if the store is somehow empty.
          const canonicalOrder = storedCanonicalRef.current ?? storedCanonical ?? (() => {
            const ehrCtx = authFlow === 'epic' && state ? mapEpicContextToEhrContext(state) : null
            return initCanonicalFromOrderFormInfo(
              dataToSubmit,
              testOptions,
              ehrCtx,
              getLabName(config)
            )
          })()
          // A draft still queued behind this PATCH holds an older snapshot and would land after it.
          cancelAutosave()
          await providerClient.patchOrderingSession(effectiveSessionId, {
            state: { canonicalOrder: normalizeCanonicalOrderForSessionPersist(canonicalOrder) },
          })
        }
        span.setAttribute('order.action', createdHere ? 'session-created-before-review' : 'session-patch-before-review')
        span.setAttribute('session.id', effectiveSessionId)

        navigate(`/order/ready?sessionId=${effectiveSessionId}`, {
          state: {
            epicState: state,
            orderFormInfoData: dataToSubmit,
            sessionId: effectiveSessionId,
            authFlow,
            configVersion: domainConfigVersion || undefined,
            orderingDomainTests: orderingDomainTestLines,
          },
        })
      } catch (error) {
        Sentry.captureException(error, { tags: { operation: 'order.review.persist' } })
        // GEN-5738: a 422 mentioning the ordering site (stale pinned config racing
        // the client allowlist) gets a specific message; otherwise the generic
        // save-failure banner. The session client throws a generic Error whose
        // message embeds the status + body text, so we match on that.
        const message = error instanceof Error ? error.message : ''
        if (
          orderSiteTrackingEnabled &&
          message.includes('422') &&
          /ordering[_\s]?site|orderingSite/i.test(message)
        ) {
          setOrderActionError('Selected ordering site is not valid for this organization.')
        } else {
          setOrderActionError("Couldn't save the order. Try again.")
        }
        // If the deferred create failed here, re-arm so a later edit (dirty-watch)
        // can still create the session — advancing to review disarmed it above.
        if (!sessionIdRef.current && createDeferredSessionRef.current) {
          pendingCreateArmedRef.current = true
        }
      } finally {
        setIsSubmittingForReview(false)
      }
    })
  }


  const handleMergedSummaryUndo = async (snapshot: ClinicalSummarySnapshot): Promise<void> => {
    cancelPendingSave()
    const reloadGeneration = mergedDraftReloadGenerationRef.current
    const queue = mergedDraftQueueRef.current?.queue
    await queue?.whenIdle()
    if (reloadGeneration !== mergedDraftReloadGenerationRef.current) {
      throw new Error('Draft changed while waiting to restore')
    }
    const current = appStore.getState().canonicalOrder.canonicalOrder
    if (!current) throw new Error('No draft is available to restore')
    const restored: CanonicalOrderPayload = {
      ...current,
      clinical: {
        ...current.clinical,
        clinicalNote: snapshot.summary,
      },
      reason: {
        ...current.reason,
        clinicalDiagnosis: snapshot.clinicalDiagnosis,
        userInteracted: snapshot.clinicalDiagnosis.length > 0,
        clinicalSummaryReview:
          snapshot.review ?? reviewForUnreviewedDiagnosis(snapshot.clinicalDiagnosis, snapshot.summary),
      },
    }
    const saved = await autoSaveCanonical(restored)
    if (!saved) throw new Error('Draft restore was not saved')
    dispatch(setCanonicalOrder(restored))
    setClinicalSummary((currentSummary) => ({ ...currentSummary, summary: snapshot.summary }))
    setIndications({
      phenotypes: snapshot.clinicalDiagnosis.map((term) => ({
        id: term.id,
        name: term.label,
        hpoCode: term.id,
        type: mapCategoryToType(term.category),
      })),
    })
  }


  const clinicalEditorControls = useRef<ClinicalSummaryEditorControls>(null)
  const latestClinicalSummary = useRef(clinicalSummary)
  latestClinicalSummary.current = clinicalSummary
  /** The section as it was when the modal opened, for Discard Changes. */
  const clinicalModalBaseline = useRef<{
    summary: ClinicalSummaryData
    reason: {
      clinicalDiagnosis: CategorizedHPOOption[]
      userInteracted: boolean
      clinicalSummaryReview: ClinicalSummaryReview | null
    }
  } | null>(null)
  useEffect(() => {
    if (!clinicalModalOpen) return
    const reason = appStore.getState().canonicalOrder.canonicalOrder?.reason
    clinicalModalBaseline.current = {
      summary: latestClinicalSummary.current,
      reason: {
        clinicalDiagnosis: reason?.clinicalDiagnosis ?? [],
        userInteracted: reason?.userInteracted ?? false,
        clinicalSummaryReview: reason?.clinicalSummaryReview ?? null,
      },
    }
    // A conflict reload bumps the epoch with the modal open; Discard then returns to the server copy.
  }, [clinicalModalOpen, clinicalSummaryEditorEpoch, appStore])

  const saveAndCloseClinicalModal = () => {
    clinicalEditorControls.current?.keepPendingExtraction()
    setClinicalModalOpen(false)
  }

  const discardClinicalModal = () => {
    setClinicalModalOpen(false)
    const baseline = clinicalModalBaseline.current
    if (!baseline) return
    const reason = appStore.getState().canonicalOrder.canonicalOrder?.reason
    const unchanged =
      JSON.stringify(baseline.summary) === JSON.stringify(latestClinicalSummary.current) &&
      JSON.stringify(baseline.reason.clinicalDiagnosis) === JSON.stringify(reason?.clinicalDiagnosis ?? []) &&
      JSON.stringify(baseline.reason.clinicalSummaryReview) === JSON.stringify(reason?.clinicalSummaryReview ?? null)
    if (unchanged) return
    const { summary, reason: baselineReason } = baseline
    setClinicalSummary(summary)
    dispatch(
      mergeClinical({
        clinicalNote: summary.summary,
        differentialDiagnosis: summary.differentialDiagnosis?.trim() || null,
        priorGeneticTesting: summary.priorGeneticTesting?.trim() || null,
      })
    )
    dispatch(
      mergeReason({
        clinicalDiagnosis: baselineReason.clinicalDiagnosis,
        userInteracted: baselineReason.userInteracted,
      })
    )
    dispatch(setClinicalSummaryReview(baselineReason.clinicalSummaryReview))
    setIndications({
      phenotypes: baselineReason.clinicalDiagnosis.map((term) => ({
        id: term.id,
        name: term.label,
        hpoCode: term.id,
        type: mapCategoryToType(term.category),
      })),
    })
    // Live extraction may already have saved the discarded edit; save the restored copy now, not after the debounce.
    const restored = appStore.getState().canonicalOrder.canonicalOrder
    // Hand the write to the hook: the autosave baseline moves forward so the dispatches
    // above do not read as a fresh edit worth a second PATCH, and the save indicator
    // reports this write rather than the superseded one it replaces.
    markCanonicalSaved(restored, autoSaveCanonical)
    // Rebuild the editor from the restored canonical on the next open: the text
    // and marks live in editor state, so without a remount Discard leaves the
    // abandoned summary on screen over a reverted store. It also retires the
    // epoch an in-flight extraction captured, so its late review cannot land.
    setClinicalSummaryEditorEpoch((epoch) => epoch + 1)
  }

  const clinicalModalSubtitle = [
    orderStartPatientName,
    formatAge(storedCanonical?.patient?.dateOfBirth).replace(/ old$/, ''),
  ]
    .filter(Boolean)
    .join(' • ')

  const handleFamilyHistorySave = (newData: FamilyHistorySummaryData) => {
    setFamilyHistorySummary(newData)
    if (newData.includeFamilyHistory) {
      dispatch(
        mergeFamily({
          familyHistory: newData.summary,
          hasParent: newData.familyMembersWithPhenotypes.parent,
          hasGrandparent: newData.familyMembersWithPhenotypes.grandparent,
          hasSibling: newData.familyMembersWithPhenotypes.sibling,
          hasCousin: newData.familyMembersWithPhenotypes.cousin,
        }),
      )
    } else {
      dispatch(
        mergeFamily({ familyHistory: null, hasParent: false, hasGrandparent: false, hasSibling: false, hasCousin: false }),
      )
    }
  }


  /**
   * Merge the guardian contact fetched during the initial patient-data load
   * (held in `pendingEpicGuardianRef`) into a freshly-seeded canonical. This is
   * a PURE READ of the ref: it does NOT null the ref and does NOT release the
   * edit lock. The ref is only consumed by `commitGuardianPrefill` AFTER the
   * session persist (create / resume PATCH) actually succeeds — so if the
   * persist fails, org resolution bails, or the effect retries on an `orgId`
   * change, the pending prefill survives and re-applies on the next attempt
   * instead of being silently lost.
   *
   * Guards (never clobber): return the canonical unchanged when it already
   * carries a substantive guardian (a prior save on a resumed session) or a
   * comparator already owns the primary guardian (the slice intentionally nulls
   * the standalone card in that case).
   */
  const withPendingEpicGuardian = useCallback(
    (seeded: CanonicalOrderPayload): CanonicalOrderPayload => {
      const guardian = pendingEpicGuardianRef.current
      if (!guardian) return seeded
      if (guardianContactHasSubstantiveContact(seeded.guardianContact)) return seeded
      if ((seeded.comparators ?? []).some((c) => c?.isPrimaryGuardian === true)) return seeded
      return { ...seeded, guardianContact: guardian }
    },
    []
  )

  /**
   * Consume the pending Epic guardian and release the edit lock. Call this ONLY
   * after the canonical carrying the (possibly merged) guardian has been durably
   * persisted (session created, or resume PATCH resolved / skipped as
   * unnecessary). Idempotent. This is the single point that ends the prefill
   * lifecycle on success, so the Guardian Contact section becomes editable only
   * once the Epic value is actually saved.
   */
  const commitGuardianPrefill = useCallback(() => {
    pendingEpicGuardianRef.current = null
    setGuardianPrefillLoading(false)
  }, [])

  /**
   * Release ONLY the edit lock, leaving the pending guardian ref intact, on
   * create/resume paths that stop before a durable persist (org-resolution bail,
   * create/patch error). These paths only re-run if an effect dependency
   * changes, so holding the lock here could strand the Guardian Contact section
   * on the "retrieving" status forever with no retry. Releasing the lock makes
   * the section editable, while keeping the ref means a genuine dep-change retry
   * can still merge the Epic value (and `withPendingEpicGuardian`'s guards skip
   * it if the provider has since entered a substantive guardian).
   */
  const releaseGuardianLockKeepPending = useCallback(() => {
    setGuardianPrefillLoading(false)
  }, [])

  // ── Effect: auto-open the New Order modal ONCE on a fresh order when the org
  // tracks ordering sites (GEN-5738). The site selector lives in the modal, and
  // the draft-create POST is gated on a valid site — so we prompt for it up front.
  //
  // This deliberately fires at most once per mount (guarded by `siteAutoOpenedRef`)
  // and does NOT depend on `patientDemographicsEditOpen`. Watching that flag would
  // re-open the modal the instant the user dismisses it (Cancel / X / backdrop)
  // while no valid site is chosen — trapping the dialog open. Dismissal is allowed;
  // the create gate + disabled submit still prevent progressing without a site, and
  // the provider can reopen via the header "Edit patient" affordance.
  const siteAutoOpenedRef = useRef(false)
  // GEN-5738: set true once the create effect's patient-lookup has resolved
  // (draft found or not). The auto-open below waits on this so an Epic launch for
  // a site-tracking org does NOT flash the demographics modal before a resumed
  // draft can hydrate its server-stored ordering site — which would both force an
  // unnecessary prompt and risk overwriting the stored site.
  const siteResumeSettledRef = useRef(false)
  useEffect(() => {
    if (!orderSiteTrackingEnabled) return
    if (sessionId || isCreatingSession.current) return
    if (orderingSiteSelectionValid) return
    if (!orderFormInfoData?.patient) return
    // Hold until the patient-draft lookup has settled. A resumed draft may carry a
    // valid site (hydrated into Redux → orderingSiteSelectionValid), which makes
    // the prompt unnecessary; opening before then would clobber that flow.
    if (!siteResumeSettledRef.current) return
    if (siteAutoOpenedRef.current) return
    siteAutoOpenedRef.current = true
    setPatientDemographicsEditOpen(true)
  }, [
    orderSiteTrackingEnabled,
    sessionId,
    orderingSiteSelectionValid,
    orderFormInfoData,
    // `siteResumeSettledRef` is a ref (not reactive); the create effect sets it and
    // then updates state (setSessionId / setCanonicalOrder) or, on a miss, arms the
    // create — each triggers a re-render that re-evaluates this effect.
  ])

  // ── Effect: create session on mount as soon as patient data is ready ──
  // Uses initCanonicalFromOrderFormInfo to seed the session without a formData useMemo.
  useEffect(() => {
    // Do not re-run once a session exists, a create is in flight/underway, patient
    // data isn't ready — or once a deferred Epic create is already armed. After the
    // deferred branch below seeds Redux and arms the create, it releases
    // `isCreatingSession` with `sessionId` still null; without the armed guard a
    // change to `orderFormInfoData` / `orgId` / `state` (this effect's deps) would
    // re-enter and re-seed Redux from a fresh canonical — wiping in-progress edits
    // and resetting the dirty baseline so a persist-worthy change is absorbed
    // without ever creating a session. Once armed, the dirty-watch effect and the
    // review CTA own the create lifecycle.
    if (
      sessionId ||
      isCreatingSession.current ||
      createInFlightRef.current ||
      pendingCreateArmedRef.current ||
      !orderFormInfoData
    ) {
      return
    }
    // For Epic launches, wait until AuthorizationContext is ready (orgId is the client
    // config key, e.g. 11112; backend matches JWT via client onboarding imdb_organization_id).
    if (authorizationLoading) {
      return
    }
    // GEN-5738: the ordering-site requirement is enforced INSIDE performCreate
    // (the actual POST /ordering/sessions), NOT here. Gating the whole effect on
    // a valid site would also block the patient-lookup resume below — so an Epic
    // launch for a site-tracking org could not look up an existing draft until a
    // site was chosen locally, forcing the demographics modal and risking
    // overwrite of the site already stored on the resumed session. Letting the
    // lookup run first means a resumed draft hydrates its server-stored site
    // (via setCanonicalOrder → selectedOrderingSite), and only a genuine new
    // create is held until a valid site exists.
    isCreatingSession.current = true
    const createSession = async () => {
      try {
        const providerClient = ProviderAPIClient.getInstance()
        const dataWithProvider = getOrderFormInfoWithProviderId(orderFormInfoData)
        const ehrCtx = authFlow === 'epic' && state ? mapEpicContextToEhrContext(state) : null
        const freshCanonical = initCanonicalFromOrderFormInfo(
          dataWithProvider,
          testOptions,
          ehrCtx,
          getLabName(config)
        )

        // When BaseCallback loaded a legacy IMDB DRAFT and dispatched setCanonicalOrder
        // before navigation, storedCanonical holds the draft's form content. We keep the
        // fresh FHIR-sourced patient/institution/providers but carry forward content fields
        // (test selection, HPO, clinical, family) so the draft is not silently discarded.
        //
        // D5 (GEN-5383 arch-review): keying this solely on `selectedOption != null`
        // wiped legacy drafts that carry content (clinical note, family, reason,
        // comparators, consents) but no test type — Epic relaunch took the DEFER
        // branch and `setCanonicalOrder(freshCanonical)` erased the draft. Use the
        // shared `canonicalHasProviderAuthoredContent` predicate so ANY
        // provider-authored content routes to the resume / carry-forward (eager)
        // branch. Guardian is not relevant for a stored draft here (pass false): a
        // bare Epic-prefill guardian with no other content is still a new order.
        // A truly empty / null stored canonical → false (defer), as today.
        const hasDraftContent = canonicalHasProviderAuthoredContent(storedCanonical, {
          guardianUserTouched: false,
          // S-1 (arch-review re-check): a bare Epic prefill persisted with only the
          // enforced hidden-slot consent defaults must stay DEFER, not be
          // reclassified as a resumable draft. Pass slot applicability so an enforced
          // `false` on a non-applicable slot does not read as draft content.
          consentApplicability: {
            ny: comparatorParentConsentConfigs.ny?.isApplicable === true,
            research: comparatorParentConsentConfigs.research?.isApplicable === true,
          },
        })
        const canonicalOrder: CanonicalOrderPayload = hasDraftContent
          ? carryForwardDraftContentOntoCanonical(
              freshCanonical,
              storedCanonical!,
              freshCanonical.order?.labName
            )
          : freshCanonical

        // GEN-5738: seed the chosen ordering site into the canonical BEFORE it is
        // persisted, so it is present in both `initialState.canonicalOrder` (new
        // session) and the resume PATCH. `performCreate` gates its POST on a valid
        // selection, so when the create path runs for a tracking org this value is
        // a valid site; on resume the server-stored site is preserved (below).
        if (orderSiteTrackingEnabled && selectedOrderingSite) {
          canonicalOrder.orderingSite = selectedOrderingSite
        }

        // The sample type / test type baked into `canonicalOrder` above are the
        // values captured when this async effect started. If the user picked a
        // sequencing option or sample type in the Test Options modal during the
        // create/lookup round-trip (while `storedCanonical` was still null, so
        // their `mergePatient` / `mergeOrderSelection` writes no-oped), those
        // picks are not in the snapshot. Replay them from the always-current
        // ref right after we seed Redux, so a fast pick is not silently lost /
        // absent on refresh. No-op when nothing changed during the round-trip.
        //
        // Split into two phases with distinct semantics for the deferred baseline:
        //  - `applySystemReconcile` replays a sample-type default/drift that the UI
        //    settled during the async lookup. A collection type carries NO
        //    provider-authored intent (it is a system-derived default), so folding
        //    it into the deferred baseline below prevents it from reading as a
        //    spurious "user edit" that fires a bare-seed create.
        //  - `applyUserPick` replays a Test-Options TEST TYPE pick the provider made
        //    during the lookup. That IS substantive content, so it must remain a
        //    genuine divergence from the baseline and arm/fire the deferred create
        //    (GEN-5383 guarantee).
        // Read `testOptionsRef.current` at APPLY time (not a snapshot taken here,
        // before the async draft lookup below): a test-type / sample-type pick the
        // provider makes DURING the lookup round-trip updates the ref, and every
        // caller of these runs after the await. A pre-await snapshot would silently
        // drop that pick (the deferred seed would keep the config default and D1 —
        // which reads the live ref — would not null it, persisting the wrong test
        // type).
        const applySystemReconcile = () => {
          const latestSampleType = testOptionsRef.current.sampleType
          if (latestSampleType !== testOptions.sampleType) {
            dispatch(
              mergePatient({ collectionType: mapSampleTypeToCollectionType(latestSampleType) || null })
            )
          }
        }
        const applyUserPick = () => {
          const latestTestType = testOptionsRef.current.testType?.trim()
          if (latestTestType && latestTestType !== testOptions.testType?.trim()) {
            const isValid = config.testTypes?.some((t) => t.value === latestTestType)
            if (!isValid) return
            // Mirror the D1/D2 untouched-config-default guard (arch-review Medium:
            // "live test pick skips default guard"). When mount `testOptions.testType`
            // was empty/invalid, the `testType validation reset` effect can write the
            // CONFIG DEFAULT into the ref DURING the lookup — a SYSTEM write, not a
            // provider pick. Dispatching it here (after the baseline) would diverge
            // the baseline and, because the seed-time D1 flag never fired (seed
            // selectedOption was null), `performCreate` would see it as authored and
            // POST a bare seed. So on the DEFERRED Epic path, do NOT dispatch a value
            // that is merely the untouched config default. A genuine provider pick of
            // a non-default type still dispatches; a default the provider truly wants
            // is persisted later (D2 backfill / explicit content), and the backstop
            // still treats a default-only seed as untouched.
            const isUntouchedConfigDefault =
              hasConfiguredDefaultTestType && latestTestType === defaultTestType?.trim()
            if (
              launchMode === 'epic_smart' &&
              !hasDraftContent &&
              !sessionIdRef.current &&
              isUntouchedConfigDefault
            ) {
              return
            }
            dispatch(
              mergeOrderSelection({ selectedOption: latestTestType, labName: getLabName(config) || null })
            )
          }
        }
        const reconcileLatestTestOptions = () => {
          applySystemReconcile()
          applyUserPick()
        }

        const lookupId = resolvePatientIdForDraftLookup({
          launchMode,
          orderFormInfoData,
          epicOAuthState: state,
        })

        if (lookupId && isDiscardMarkerSet(lookupId)) {
          isCreatingSession.current = false
          releaseGuardianLockKeepPending()
          return
        }

        let existing: Record<string, unknown> | null = null
        if (lookupId) {
          try {
            // Preflight: GET /ordering/sessions/exists returns 200 { exists }
            // for both hit and miss, so the "new patient" case no longer emits
            // the 404 that GET /ordering/sessions produces on a miss. Only fetch
            // the full session when the preflight confirms one exists.
            const sessionExists = await providerClient.orderingSessionExists(lookupId)
            if (sessionExists) {
              existing = await providerClient.getOrderingSessionByPatient(lookupId)
            }
          } catch (lookupErr) {
            Sentry.captureException(lookupErr, { tags: { operation: 'order.session.lookup_by_patient' } })
            existing = null
          }
        }

        // GEN-5738: mark the resume lookup as settled so the auto-open effect may
        // now prompt for a site if one is still needed. A resumed draft hydrates
        // its server-stored site below (setCanonicalOrder), which flips
        // orderingSiteSelectionValid true and suppresses the prompt entirely.
        siteResumeSettledRef.current = true

        if (existing) {
          const newId = existing['sessionId'] as string
          const rawState = (existing['state'] as Record<string, unknown> | undefined) ?? {}
          const serverCanonical = rawState['canonicalOrder'] as CanonicalOrderPayload | undefined

          let finalCanonical: CanonicalOrderPayload = canonicalOrder
          if (serverCanonical) {
            finalCanonical = hasDraftContent
              ? carryForwardDraftContentOntoCanonical(
                  serverCanonical,
                  storedCanonical!,
                  freshCanonical.order?.labName
                )
              : serverCanonical
          }

          // GEN-5738: the branch above can adopt the server payload wholesale
          // (when there is no local draft content). On resume the server-stored
          // ordering site is AUTHORITATIVE — a provider who relaunches for the
          // same patient must not clobber the site the draft was created with
          // (the user explicitly flagged this overwrite risk). So only fill the
          // site from a local pick when the resumed draft has NO site yet (legacy
          // drafts created before site tracking, or a draft whose create was
          // still pending). When we do fill it, force a resume PATCH so the value
          // is durably persisted (backend allowlist-validates + requires it for
          // tracked orgs). `siteApplied` is only true when we actually set it, so
          // untracked orgs and already-sited sessions don't trigger an extra PATCH.
          let siteApplied = false
          if (
            orderSiteTrackingEnabled &&
            !finalCanonical.orderingSite &&
            pendingOrderingSite &&
            isValidOrganizationSite(pendingOrderingSite, config)
          ) {
            finalCanonical = { ...finalCanonical, orderingSite: pendingOrderingSite }
            siteApplied = true
          }

          // Merge the Epic-fetched guardian BEFORE seeding + persisting so the
          // prefill lands in the session state (survives refresh), not just
          // Redux. `guardianApplied` forces a resume PATCH even when there is no
          // other draft content, so a guardian-only prefill is durably saved.
          // The ref is a pure read here; it is consumed by `commitGuardianPrefill`
          // only after the PATCH decision resolves below.
          const beforeGuardian = finalCanonical.guardianContact ?? null
          finalCanonical = withPendingEpicGuardian(finalCanonical)
          const guardianApplied = (finalCanonical.guardianContact ?? null) !== beforeGuardian

          dispatch(setCanonicalOrder(finalCanonical))
          setSessionId(newId)
          reconcileLatestTestOptions()
          setSearchParams((prev) => {
            prev.set('sessionId', newId)
            return prev
          }, { replace: true })

          if (hasDraftContent || guardianApplied || siteApplied) {
            try {
              await providerClient.patchOrderingSession(newId, {
                state: { canonicalOrder: normalizeCanonicalOrderForSessionPersist(finalCanonical) },
              })
            } catch (patchErr) {
              Sentry.captureException(patchErr, { tags: { operation: 'order.session.resume_patch' } })
            }
          }

          // Session exists and the guardian (if any) has been seeded + persist
          // attempted → consume the pending ref and release the edit lock.
          commitGuardianPrefill()

          await lockPinnedConfig(existing['configId'])

          Sentry.logger.info('Ordering session resumed by patient id', {
            sessionId: newId,
            patientLookupId: lookupId,
            mergedReduxDraft: hasDraftContent,
          })
          return
        }

        // ── No existing draft for this patient ──
        // `performCreate` runs the actual POST /ordering/sessions. It is defined
        // as a closure so it can be invoked either eagerly (standalone flow, and
        // any retry after an org-resolution bail) or lazily (Epic flow, on the
        // first substantive edit / advancing to review). It is the single owner
        // of the create: both triggers (dirty-watch effect and the review CTA)
        // route through it, and its in-flight guard makes a concurrent /
        // double-invoked call a no-op rather than a second POST.
        const performCreate = async (): Promise<string | null> => {
          // Idempotency guard: never issue a second POST. If a session already
          // exists (this call raced another that finished) return it; if a create
          // is already in flight, AWAIT it and return its sessionId — do not return
          // null (that would make the review CTA falsely report "still initializing"
          // while a POST is genuinely underway).
          if (sessionIdRef.current) {
            return sessionIdRef.current
          }
          if (createInFlightRef.current) {
            return createInFlightPromiseRef.current ?? sessionIdRef.current ?? null
          }
          // Correctness backstop (field-agnostic): on the DEFERRED Epic path, never
          // POST for an untouched launch seed. The dirty-watch can be woken by a
          // mount-time SYSTEM dispatch (sample-type reconcile, consent enforcement)
          // that is not provider intent — the confirmed production regression
          // (single INSERT, no provider content). Bail like the retryable org/site
          // bails: keep ARMED + baseline, do NOT set in-flight, do NOT throw, return
          // null, so a later GENUINE edit still creates. Standalone / resume paths
          // (`hasDraftContent`) are unaffected.
          if (launchMode === 'epic_smart' && !hasDraftContent) {
            const liveCanonical = storedCanonicalRef.current ?? canonicalOrder
            if (
              isUntouchedLaunchSeed(liveCanonical, guardianUserTouchedRef.current, false, {
                ny: comparatorParentConsentConfigs.ny?.isApplicable === true,
                research: comparatorParentConsentConfigs.research?.isApplicable === true,
              })
            ) {
              releaseGuardianLockKeepPending()
              pendingCreateArmedRef.current = true
              return null
            }
          }
          // GEN-5738: the provider-api REQUIRES canonicalOrder.orderingSite at
          // draft-create for site-tracking orgs (POST 422s without it) and
          // allowlist-validates the value. This is the ONLY branch that must hold
          // for a site — the resume branch above already ran, so an existing draft
          // (with its server-stored site) is never blocked here. Read the site
          // from a ref (not the closure) because the Epic deferred `performCreate`
          // is captured once and would otherwise gate on a stale, pre-selection
          // value forever. When no valid site is chosen yet, bail retryably:
          // re-arm the deferred create for Epic launches so picking a site fires
          // the POST. Do NOT set the in-flight flag, so the retry isn't swallowed.
          if (orderSiteTrackingEnabled && !orderingSiteSelectionValidRef.current) {
            releaseGuardianLockKeepPending()
            if (launchMode === 'epic_smart' && !hasDraftContent) {
              pendingCreateArmedRef.current = true
            }
            return null
          }
          createInFlightRef.current = true
          // Capture the create work in a promise stored on a ref so a concurrent
          // caller (the in-flight branch above) can await THIS create and receive
          // its sessionId. A per-create identity token lets the finally clear both
          // the in-flight flag and the shared promise (only if a later retry hasn't
          // already replaced it) so a subsequent retry can start fresh.
          const createToken = {}
          createInFlightTokenRef.current = createToken
          const runCreate = async (): Promise<string | null> => {
          try {
            const resolvedOrgId = await resolveImdbOrgIdForOrderingApi({
              orgIdFromAuth: orgId,
              isEpicFlow: launchMode === 'epic_smart',
              epicOAuthState: state || undefined,
            })
            if (!resolvedOrgId) {
              if (import.meta.env.DEV) {
                console.warn(
                  '[OrderStart] Skipping POST /ordering/sessions: organizationId is not an IMDB UUID. ' +
                    'After patient lookup 404, a new draft is only created when org matches the JWT (epic-provision / Auth0).',
                  { orgIdFromContext: orgId }
                )
              }
              // Retryable bail (orgId may resolve later). Keep the pending
              // guardian and release the edit lock so the section isn't stranded.
              // Critically, RE-ARM the deferred create so a subsequent edit
              // retries — the create effect's deps do not include an edit signal,
              // so without re-arming the order would be stranded unpersistable.
              releaseGuardianLockKeepPending()
              if (launchMode === 'epic_smart' && !hasDraftContent) {
                pendingCreateArmedRef.current = true
              }
              return null
            }
            if (import.meta.env.DEV && orgId && orgId !== resolvedOrgId) {
              traceOrderStart('create session org resolved from JWT', { orgIdFromContext: orgId, resolvedOrgId })
            }

            // Merge the Epic-fetched guardian BEFORE building `initialState` so the
            // prefill is persisted with the session (survives refresh). Use the live
            // Redux canonical when present (a deferred create captures post-launch
            // edits); fall back to the launch snapshot for the eager standalone path.
            // Skip once the provider has touched the guardian section (e.g. cleared
            // it) — re-applying would silently revert their edit.
            const baseForPersist = storedCanonicalRef.current ?? canonicalOrder
            const canonicalToPersist = guardianUserTouchedRef.current
              ? baseForPersist
              : withPendingEpicGuardian(baseForPersist)
            // withPendingEpicGuardian merges into the payload only, not Redux. If it
            // added/changed a guardian vs the live store, that guardian must be
            // synced into Redux (and the flush) below.
            const guardianMergedAtPersist =
              (canonicalToPersist.guardianContact ?? null) !== (baseForPersist.guardianContact ?? null)

            const result = await providerClient.createOrderingSession({
              organizationId: resolvedOrgId,
              ...(domainConfigVersion ? { configVersion: domainConfigVersion } : {}),
              initialState: { canonicalOrder: normalizeCanonicalOrderForSessionPersist(canonicalToPersist) },
            })
            const newId = result['sessionId'] as string
            // Seed Redux ONLY when the store is empty (eager standalone path needs a
            // non-null base for later merge* actions). Never clobber a populated
            // store: on the deferred/eager-resume paths it holds the seed plus any
            // edits made during the POST window, and the flush below reconciles it.
            if (!storedCanonicalRef.current) {
              dispatch(setCanonicalOrder(canonicalToPersist))
            } else if (guardianMergedAtPersist) {
              // Populated store: sync the persist-time guardian via a guardian-ONLY
              // dispatch so it lands in Redux without clobbering concurrent edits.
              dispatch(setGuardianContact(canonicalToPersist.guardianContact ?? null))
            }
            setSessionId(newId)
            sessionIdRef.current = newId
            reconcileLatestTestOptions()
            setSearchParams((prev) => { prev.set('sessionId', newId); return prev }, { replace: true })
            // Session created with the guardian in its initialState → consume the
            // pending ref and release the edit lock.
            commitGuardianPrefill()
            // Deferred-create is satisfied: clear the armed flag + baseline so the
            // dirty-watch effect stops trying to create.
            pendingCreateArmedRef.current = false
            deferredCreateBaselineRef.current = null
            // Flush the live canonical when it diverges from what the POST persisted
            // (an edit that landed during the POST window, or the eager seed). Build
            // it from the live store but re-apply the persist-time guardian, since
            // `storedCanonicalRef` is a render-mirror that hasn't reflected the
            // setGuardianContact dispatch yet — otherwise the flush would overwrite
            // the just-persisted guardian while still preserving concurrent edits.
            // Autosave is inert until `sessionId` exists (and won't fire on the
            // enabling transition alone), so this PATCH closes the create-window write.
            const liveStore = storedCanonicalRef.current
            const liveNow =
              liveStore && guardianMergedAtPersist
                ? { ...liveStore, guardianContact: canonicalToPersist.guardianContact ?? null }
                : liveStore
            if (
              liveNow &&
              JSON.stringify(normalizeCanonicalOrderForSessionPersist(liveNow)) !==
                JSON.stringify(normalizeCanonicalOrderForSessionPersist(canonicalToPersist))
            ) {
              try {
                await providerClient.patchOrderingSession(newId, {
                  state: { canonicalOrder: normalizeCanonicalOrderForSessionPersist(liveNow) },
                })
              } catch (flushErr) {
                Sentry.captureException(flushErr, { tags: { operation: 'order.session.create_flush' } })
              }
            }
            // D4 (GEN-5383 arch-review): record what the create path ultimately
            // persisted (the flush payload if it diverged, else the POST payload)
            // and mark this as a LOCALLY-created session, so the create→session
            // reconcile effect can PATCH exactly once if an edit landed in the
            // window between the flush snapshot above and React committing
            // `setSessionId` (autosave does not fire on its enabling transition).
            // A resumed / hydrated session never sets these, so the reconcile is
            // scoped to the local-create transition only.
            lastPersistedCreatePayloadRef.current = JSON.stringify(
              normalizeCanonicalOrderForSessionPersist(liveNow ?? canonicalToPersist)
            )
            locallyCreatedSessionIdRef.current = newId
            await lockPinnedConfig(result['configId'])
            Sentry.logger.info('Ordering session created', {
              sessionId: newId,
              resumedDraft: hasDraftContent,
              deferred: launchMode === 'epic_smart' && !hasDraftContent,
            })
            return newId
          } finally {
            createInFlightRef.current = false
            if (createInFlightTokenRef.current === createToken) {
              createInFlightPromiseRef.current = null
              createInFlightTokenRef.current = null
            }
            // Nudge the create→session reconcile effect to re-run now that the
            // create has fully settled: it may have already fired and bailed on the
            // `createInFlightRef` guard (React can flush the `setSessionId` re-render
            // during the awaited flush / lockPinnedConfig above), and nothing else
            // re-renders afterward. The reconcile is still idempotent via
            // `createReconcileDoneRef`.
            setCreateSettledTick((t) => t + 1)
          }
          }
          const createPromise = runCreate()
          createInFlightPromiseRef.current = createPromise
          return await createPromise
        }

        // GEN-6020: an Epic launch for a patient whose most recent order was
        // cancelled offers to restart it. Choosing either option is provider
        // intent, so the draft is created now (not deferred) and hydrated like a
        // resumed draft; dismissing the prompt falls through to a plain new order.
        if (launchMode === 'epic_smart' && !hasDraftContent && lookupId) {
          try {
            const result = await offerRestartOfCancelledOrder({
              patientLookupId: lookupId,
              epicAccessToken: state ? EpicAuthClient.getInstance().getAccessToken(state)?.trim() || null : null,
              promptForMode: (eligibility) =>
                new Promise<RestartOrderMode | null>((resolve) => {
                  restartChoiceRef.current = resolve
                  setRestartPrompt(eligibility)
                }),
              createSession: async (restart) => {
                const resolvedOrgId = await resolveImdbOrgIdForOrderingApi({
                  orgIdFromAuth: orgId,
                  isEpicFlow: true,
                  epicOAuthState: state || undefined,
                })
                if (!resolvedOrgId) throw new Error('Restart: organization could not be resolved')
                // The server overlays non-empty launch values on the carried order, so
                // Keep must not send the seed's test selection (often the config
                // default) or it would replace the cancelled order's test.
                const launchSeed =
                  restart.restartMode === 'keep'
                    ? { ...canonicalOrder, order: { ...canonicalOrder.order, selectedOption: null } }
                    : canonicalOrder
                return providerClient.createOrderingSession({
                  organizationId: resolvedOrgId,
                  ...(domainConfigVersion ? { configVersion: domainConfigVersion } : {}),
                  initialState: {
                    canonicalOrder: normalizeCanonicalOrderForSessionPersist(withPendingEpicGuardian(launchSeed)),
                  },
                  ...restart,
                })
              },
            })
            if (result) {
              const newId = result['sessionId'] as string
              const restartedState = (result['state'] as Record<string, unknown> | undefined) ?? {}
              const restartedCanonical = restartedState['canonicalOrder'] as CanonicalOrderPayload | undefined
              if (!restartedCanonical) throw new Error('Restart: session returned without an order')
              dispatch(setCanonicalOrder(restartedCanonical))
              // Before `sessionIdRef` is set, so a config default written during the
              // prompt does not replace the carried order's test.
              reconcileLatestTestOptions()
              setSessionId(newId)
              sessionIdRef.current = newId
              setSearchParams((prev) => {
                prev.set('sessionId', newId)
                return prev
              }, { replace: true })
              commitGuardianPrefill()
              await lockPinnedConfig(result['configId'])
              Sentry.logger.info('Ordering session restarted from cancelled order', { sessionId: newId })
              return
            }
          } catch (restartErr) {
            const outcome = restartFailureOutcome(restartErr)
            if (outcome.kind === 'resumeDraft') {
              const draftId = outcome.sessionId
              dispatch(resetCanonicalOrder())
              setSessionId(draftId)
              sessionIdRef.current = draftId
              setSearchParams((prev) => {
                prev.set('sessionId', draftId)
                return prev
              }, { replace: true })
              releaseGuardianLockKeepPending()
              Sentry.logger.info('Restart found an open draft; resuming it', { sessionId: draftId })
              return
            }
            Sentry.captureException(restartErr, { tags: { operation: 'order.restart.create' } })
            setPatientError(outcome.message)
          }
        }

        // Defer creation for the Epic/EHR launch flow: opening the app for a
        // patient who may never qualify must NOT create a distinct order. Seed
        // the form locally and arm a deferred create; the POST happens on the
        // first substantive edit (dirty-watch effect) or when advancing to
        // review (`handleSubmitOrder`). A launch that already carries draft
        // content (legacy IMDB DRAFT reloaded by BaseCallback) is a real
        // resume, so create immediately rather than deferring.
        if (launchMode === 'epic_smart' && !hasDraftContent) {
          // D1 (GEN-5383 arch-review): on default-test-type orgs the seed is born
          // with a non-null `order.selectedOption` the provider never picked, which
          // would make the layer-2 backstop a no-op and expose a bare-seed POST. On
          // the DEFERRED Epic path only, strip that untouched config-default
          // selection so the seed is genuinely untouched. The default still lives in
          // `testOptions.testType` for the UI, and a genuine pick (during the lookup
          // via `applyUserPick`, or later via `mergeOrderSelection`) still writes a
          // non-null `selectedOption` and fires the create. Standalone / resume
          // paths are unchanged.
          const seedSelectedOption = canonicalOrder.order?.selectedOption ?? null
          const seedSelectedOptionIsConfigDefault =
            seedSelectedOption != null &&
            hasConfiguredDefaultTestType &&
            seedSelectedOption === defaultTestType &&
            // A pick made during the async lookup (reflected in the always-current
            // testOptions ref as a change from the mount default) is provider
            // intent, not the untouched config default — keep it.
            testOptionsRef.current.testType?.trim() === defaultTestType
          const deferredSeedCanonical: CanonicalOrderPayload = seedSelectedOptionIsConfigDefault
            ? { ...canonicalOrder, order: { ...canonicalOrder.order, selectedOption: null } }
            : canonicalOrder
          const seeded = withPendingEpicGuardian(deferredSeedCanonical)
          dispatch(setCanonicalOrder(seeded))
          // Replay any SYSTEM reconcile (sample-type default/drift the UI settled
          // during the async lookup) BEFORE capturing the baseline, so a
          // system-derived `patient.collectionType` is part of the baseline rather
          // than reading as a spurious "user edit" (the confirmed bare-seed bug:
          // the reconcile's `mergePatient({ collectionType })` landed AFTER the
          // baseline and fired a create of an untouched seed). No provider intent.
          applySystemReconcile()
          // Capture the dirty baseline from the live store after `setCanonicalOrder`
          // (which applied the inbound legacy-slot fold + placeholder-DOB strip) and
          // after `applySystemReconcile`, normalizing exactly as the dirty-watch
          // effect does so the two stay in lockstep. A genuine Test-Options pick is
          // applied AFTER the baseline (via `applyUserPick` below) so it still arms
          // the create (GEN-5383).
          const baselineSource =
            appStore.getState().canonicalOrder.canonicalOrder ?? seeded
          // D-1 (arch-review re-check): the ungated
          // `useEnforceNonApplicableSecondaryConsentDefaults` hook dispatches a
          // SYSTEM `mergePatient` setting hidden-slot NY/research `false` AFTER this
          // synchronous capture. Fold the same pure enforcement into the baseline
          // (same `consents` config the hook uses) so that write is not a divergence
          // — otherwise it fires a bare-seed POST. Peer to `applySystemReconcile`.
          const baselineAfterEnforcement = applyHiddenSecondaryConsentDefaults(
            baselineSource,
            comparatorParentConsentConfigs
          )
          deferredCreateBaselineRef.current = JSON.stringify(
            normalizeCanonicalOrderForSessionPersist(baselineAfterEnforcement)
          )
          applyUserPick()
          pendingCreateArmedRef.current = true
          createDeferredSessionRef.current = performCreate
          // Local seed done, no server session yet: release the guardian edit
          // lock (keeping the pending ref) so the Guardian section is editable
          // while we wait for the first edit; `performCreate` will persist +
          // commit the guardian when it eventually runs.
          releaseGuardianLockKeepPending()
          isCreatingSession.current = false
          Sentry.logger.info('Epic launch: deferring ordering session creation until first edit', {
            patientLookupId: lookupId,
          })
          return
        }

        // Standalone flow (or Epic launch resuming inline draft content): create now.
        // `performCreate` returns null on the retryable org-resolution bail
        // WITHOUT throwing, so the catch below won't run. Clear the create lock
        // here on a null result so a later effect re-run (e.g. once `orgId`
        // resolves to an IMDB UUID) can retry instead of being stranded.
        const eagerCreatedId = await performCreate()
        if (!eagerCreatedId) {
          isCreatingSession.current = false
        }
      } catch (error) {
        Sentry.captureException(error, { tags: { operation: 'order.session.create_on_mount' } })
        isCreatingSession.current = false
        // Retryable failure (effect re-runs on dep change): keep the pending
        // guardian so a retry can still apply + persist it, but release the edit
        // lock so the section isn't stranded on "retrieving" with no retry.
        releaseGuardianLockKeepPending()
      }
    }
    createSession()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderFormInfoData, authorizationLoading, orgId, launchMode, state, orderSiteTrackingEnabled, orderingSiteSelectionValid])

  // ── Effect: lazily create the deferred Epic session on the first substantive edit ──
  // For the Epic/EHR launch flow the create effect above seeds the form locally,
  // captures a normalized baseline (post inbound-transform, pre-reconcile), and
  // arms a deferred create instead of POSTing (so opening the app for a patient
  // who never qualifies leaves no order behind). Here we watch the live canonical
  // against that baseline; the first persist-worthy change (test selection,
  // clinical, indications, family, guardian, a Test-Options pick made during the
  // launch lookup, etc.) triggers the real POST /ordering/sessions. Comparison
  // uses the same normalization as autosave so cosmetic / no-op changes don't
  // create a session.
  useEffect(() => {
    if (sessionId || !pendingCreateArmedRef.current || isCreatingSession.current || createInFlightRef.current) {
      return
    }
    if (!storedCanonical) {
      return
    }
    const current = JSON.stringify(normalizeCanonicalOrderForSessionPersist(storedCanonical))
    // Defensive: if the baseline was never captured (should not happen — the
    // deferred branch always sets it before arming), capture it now from the
    // stored canonical rather than treating mount as a change (which would fire a
    // spurious create).
    if (deferredCreateBaselineRef.current == null) {
      deferredCreateBaselineRef.current = current
      return
    }
    if (current === deferredCreateBaselineRef.current) {
      return
    }
    const create = createDeferredSessionRef.current
    if (!create) {
      return
    }
    isCreatingSession.current = true
    // Disarm immediately so a second edit landing before the POST resolves does
    // not enqueue a duplicate create; `performCreate` clears the baseline too and
    // is itself guarded by `createInFlightRef` / `sessionIdRef`.
    pendingCreateArmedRef.current = false
    create()
      .catch((error) => {
        Sentry.captureException(error, { tags: { operation: 'order.session.deferred_create' } })
        // A thrown createOrderingSession error leaves no session. Re-arm so a
        // subsequent edit retries — without this, the disarm above is permanent
        // and later edits never create a draft (autosave stays off until the
        // provider happens to advance to review). The baseline is preserved
        // (performCreate clears it only on success), so the current, already-
        // diverged canonical re-triggers the create on the next render/edit.
        if (!sessionIdRef.current) {
          pendingCreateArmedRef.current = true
        }
      })
      .finally(() => {
        isCreatingSession.current = false
      })
  }, [sessionId, storedCanonical])

  // ── Effect: D4 — reconcile an edit that landed in the create→session window ──
  // (GEN-5383 arch-review.) `performCreate` snapshots the store for its flush
  // PATCH, then commits `setSessionId` (enabling autosave, which fires only on the
  // NEXT change). An edit landing between the snapshot and the autosave
  // re-subscription is neither flushed nor autosaved — lost on navigate-away. This
  // effect fires when `sessionId` first becomes set for a LOCALLY-created session,
  // re-reads the store, and PATCHes only if it differs from the last-persisted
  // payload — closing the sub-window. It can run DURING the create (React may flush
  // the `setSessionId` re-render while the awaited flush / lockPinnedConfig are
  // still pending), when it bails on `createInFlightRef`; `performCreate`'s finally
  // then bumps `createSettledTick` to re-run it. Scoped via
  // `locallyCreatedSessionIdRef` (resumed/hydrated sessions never trigger it) and
  // idempotent via `createReconcileDoneRef`.
  useEffect(() => {
    if (!sessionId) return
    if (locallyCreatedSessionIdRef.current !== sessionId) return
    if (createReconcileDoneRef.current === sessionId) return
    if (createInFlightRef.current) return
    if (!storedCanonical) return
    // Mark done up-front so a re-render during the await cannot re-enter.
    createReconcileDoneRef.current = sessionId
    const current = JSON.stringify(normalizeCanonicalOrderForSessionPersist(storedCanonical))
    if (current === lastPersistedCreatePayloadRef.current) {
      // No edit landed in the window → nothing to reconcile (no extra PATCH).
      return
    }
    const providerClient = ProviderAPIClient.getInstance()
    lastPersistedCreatePayloadRef.current = current
    // S-2 (arch-review re-check): the same `sessionId` transition also enables
    // autosave, which may schedule a debounced PATCH of this same edit. Cancel it
    // so the identical payload isn't written twice (`useAutosave` is declared
    // earlier, so its timer is already scheduled). A later genuine edit re-triggers
    // autosave normally.
    cancelAutosave()
    void providerClient
      .patchOrderingSession(sessionId, {
        state: { canonicalOrder: normalizeCanonicalOrderForSessionPersist(storedCanonical) },
      })
      .catch((reconcileErr) => {
        Sentry.captureException(reconcileErr, {
          tags: { operation: 'order.session.create_reconcile' },
        })
      })
  }, [sessionId, storedCanonical, createSettledTick, cancelAutosave])


  useEffect(() => {
    if (!sessionId) {
      return
    }
    traceOrderStart('hydrate effect enter', {
      sessionId,
      isHydrated,
      hasStoredCanonical: storedCanonical !== null,
      reduxSelectedOption: storedCanonical?.order?.selectedOption ?? null,
      hasOrderFormInfoData: Boolean(orderFormInfoData),
    })
    const isAlreadyHydrated = isHydrated && storedCanonical !== null
    if (isAlreadyHydrated) {
      // Redux already has the canonical (e.g. StrictMode remount or forward navigation).
      // Re-populate local form state from the store without a second network call.
      const fromOrderRemount = sessionStoredTestTypeOrMissing(
        storedCanonical.order?.selectedOption as string | null | undefined
      )
      const existingTestTypeRemount: TestType | '' =
        fromOrderRemount ?? (hasConfiguredDefaultTestType ? defaultTestType : '')
      sessionLoadedTestTypeRef.current = existingTestTypeRemount || null
      const existingTestConfigRemount = existingTestTypeRemount
        ? getTestTypeConfig(existingTestTypeRemount, config)
        : undefined
      const parentSamplesRequiredRemount = existingTestConfigRemount?.parentSamplesRequired ?? 0
      // C1.3: read via comparator-first so sessions that rehydrate from
      // a comparators-only payload still trigger duo auto-selection.
      const motherCompRemount = FormCompletionChecker.readParent(storedCanonical, 'mother')
      const fatherCompRemount = FormCompletionChecker.readParent(storedCanonical, 'father')
      const motherStartedRemount = Boolean(
        motherCompRemount?.demographic?.firstName || motherCompRemount?.demographic?.mrn
      )
      const fatherStartedRemount = Boolean(
        fatherCompRemount?.demographic?.firstName || fatherCompRemount?.demographic?.mrn
      )
      let motherSelectedRemount = parentSamplesRequiredRemount === 2
      let fatherSelectedRemount = parentSamplesRequiredRemount === 2
      if (parentSamplesRequiredRemount === 1) {
        const ot = (storedCanonical.orderType ?? '').trim()
        if (ot === 'FAMILY_DUO_MOTHER') {
          motherSelectedRemount = true
          fatherSelectedRemount = false
        } else if (ot === 'FAMILY_DUO_FATHER') {
          motherSelectedRemount = false
          fatherSelectedRemount = true
        } else if (motherStartedRemount && !fatherStartedRemount) {
          motherSelectedRemount = true
          fatherSelectedRemount = false
        } else if (fatherStartedRemount && !motherStartedRemount) {
          motherSelectedRemount = false
          fatherSelectedRemount = true
        } else {
          motherSelectedRemount = false
          fatherSelectedRemount = false
        }
      }
      if (parentSamplesRequiredRemount === 0) {
        motherSelectedRemount = false
        fatherSelectedRemount = false
      }

      // Always re-apply sequencing from canonical — even when orderFormInfoData is already
      // set (e.g. patient from location.state), otherwise testType stays on initial defaults.
      setTestOptions((prev) =>
        withRelationshipSelections(
          {
            ...prev,
            testType: existingTestTypeRemount,
            sampleType: storedCanonical.patient?.collectionType
              ? mapCollectionTypeToSampleType(storedCanonical.patient.collectionType as CollectionType)
              : prev.sampleType,
          },
          { MOTHER: motherSelectedRemount, FATHER: fatherSelectedRemount }
        )
      )

      // F.1c Phase 4: biological parent UI state is derived from canonical on
      // render (see `deriveBiologicalParentsFromCanonical`), so no imperative
      // hydration of mother/father local state is needed here. The selection /
      // test type the derivation reads off `testOptions` is set just above via
      // `setTestOptions(withRelationshipSelections(...))`.

      // Always sync these from canonical on remount. They were incorrectly gated on
      // `!orderFormInfoData`; patient/order form can be pre-filled from Epic or navigation
      // while canonical still holds the saved draft clinical / indications / family.
      setClinicalSummary({
        summary: storedCanonical.clinical?.clinicalNote ?? '',
        differentialDiagnosis: storedCanonical.clinical?.differentialDiagnosis ?? '',
        priorGeneticTesting: storedCanonical.clinical?.priorGeneticTesting ?? '',
      })
      const existingPhenotypesRemount = Array.isArray(storedCanonical.reason?.clinicalDiagnosis)
        ? (storedCanonical.reason?.clinicalDiagnosis as CategorizedHPOOption[])
        : []
      setIndications({
        phenotypes: existingPhenotypesRemount.map((term) => ({
          id: term.id,
          name: term.label,
          hpoCode: term.id,
          type: mapCategoryToType(term.category),
        })),
      })
      const familyDataRemount = storedCanonical.family
      if (familyDataRemount) {
        setFamilyHistorySummary((prev) => ({
          ...prev,
          includeFamilyHistory: true,
          hasInteracted: true,
          summary: familyDataRemount.familyHistory ?? '',
          familyMembersWithPhenotypes: {
            parent: familyDataRemount.hasParent ?? false,
            grandparent: familyDataRemount.hasGrandparent ?? false,
            sibling: familyDataRemount.hasSibling ?? false,
            cousin: familyDataRemount.hasCousin ?? false,
          },
        }))
      }

      if (!orderFormInfoData) {
        const partial = mapCanonicalToOrderFormInfo(storedCanonical)
        setOrderFormInfoData(partial as OrderFormInfoData)
        setPatientInfo(getPatientInfoFromForm(partial as OrderFormInfoData))
      }
      setPatientError(null)
      setSessionHydrationDone(true)
      traceOrderStart('hydrate remount-from-redux done', {
        appliedTestType: existingTestTypeRemount,
        fullPatientRestore: !orderFormInfoData,
      })
      return
    }
    const hydrateFromSession = async () => {
      try {
        const session = await getOrderingSessionCoalesced(sessionId)
        // Refresh path: lock the config revision the server pinned to this draft
        // so the rehydrated form renders against the same config as at creation.
        void lockPinnedConfig(session['configId'])
        const state_data = session['state'] as Record<string, unknown> | undefined
        const canonicalOrderData = state_data?.['canonicalOrder'] as CanonicalOrderPayload | undefined
        if (!canonicalOrderData) {
          traceOrderStart('hydrate GET no canonical in session state', { sessionId })
          setSessionHydrationDone(true)
          return
        }
        traceOrderStart('hydrate GET ok', {
          sessionId,
          apiSelectedOption: canonicalOrderData.order?.selectedOption ?? null,
          apiOrderType: canonicalOrderData.orderType,
        })
        // Populate canonical slice — all state updates below are batched by React 18
        // into a single re-render so the UI sees the hydrated canonical state immediately.
        dispatch(setCanonicalOrder(canonicalOrderData))

        // Derive test-type and parent-selection state from session data
        const fromOrder = sessionStoredTestTypeOrMissing(
          canonicalOrderData.order?.selectedOption as string | null | undefined
        )
        const existingTestType: TestType | '' = fromOrder ?? (hasConfiguredDefaultTestType ? defaultTestType : '')
        sessionLoadedTestTypeRef.current = existingTestType || null
        const existingTestConfig = existingTestType ? getTestTypeConfig(existingTestType, config) : undefined
        const parentSamplesRequired = existingTestConfig?.parentSamplesRequired ?? 0
        // C1.3: read via comparator-first so sessions that rehydrate from
        // a comparators-only payload still trigger duo auto-selection.
        const motherCompLoad = FormCompletionChecker.readParent(canonicalOrderData, 'mother')
        const fatherCompLoad = FormCompletionChecker.readParent(canonicalOrderData, 'father')
        const motherStarted = Boolean(
          motherCompLoad?.demographic?.firstName || motherCompLoad?.demographic?.mrn
        )
        const fatherStarted = Boolean(
          fatherCompLoad?.demographic?.firstName || fatherCompLoad?.demographic?.mrn
        )

        let motherSelected = parentSamplesRequired === 2
        let fatherSelected = parentSamplesRequired === 2

        if (parentSamplesRequired === 1) {
          const ot = (canonicalOrderData.orderType ?? '').trim()
          if (ot === 'FAMILY_DUO_MOTHER') {
            motherSelected = true
            fatherSelected = false
          } else if (ot === 'FAMILY_DUO_FATHER') {
            motherSelected = false
            fatherSelected = true
          } else if (motherStarted && !fatherStarted) {
            motherSelected = true
            fatherSelected = false
          } else if (fatherStarted && !motherStarted) {
            motherSelected = false
            fatherSelected = true
          } else {
            motherSelected = false
            fatherSelected = false
          }
        }

        if (parentSamplesRequired === 0) {
          motherSelected = false
          fatherSelected = false
        }

        setTestOptions((prev) =>
          withRelationshipSelections(
            {
              ...prev,
              testType: existingTestType,
              sampleType: canonicalOrderData.patient?.collectionType
                ? mapCollectionTypeToSampleType(canonicalOrderData.patient.collectionType as CollectionType)
                : prev.sampleType,
            },
            { MOTHER: motherSelected, FATHER: fatherSelected }
          )
        )

        setClinicalSummary({
          summary: canonicalOrderData.clinical?.clinicalNote ?? '',
          differentialDiagnosis: canonicalOrderData.clinical?.differentialDiagnosis ?? '',
          priorGeneticTesting: canonicalOrderData.clinical?.priorGeneticTesting ?? '',
        })

        const existingPhenotypes = Array.isArray(canonicalOrderData.reason?.clinicalDiagnosis)
          ? (canonicalOrderData.reason?.clinicalDiagnosis as CategorizedHPOOption[])
          : []
        const mappedPhenotypes =
          existingPhenotypes?.map((term) => ({
            id: term.id,
            name: term.label,
            hpoCode: term.id,
            type: mapCategoryToType(term.category),
          })) ?? []
        setIndications({ phenotypes: mappedPhenotypes })

        const familyData = canonicalOrderData.family
        if (familyData) {
          setFamilyHistorySummary((prev) => ({
            ...prev,
            includeFamilyHistory: true,
            hasInteracted: true,
            summary: familyData.familyHistory ?? '',
            familyMembersWithPhenotypes: {
              parent: familyData.hasParent ?? false,
              grandparent: familyData.hasGrandparent ?? false,
              sibling: familyData.hasSibling ?? false,
              cousin: familyData.hasCousin ?? false,
            },
          }))
        }

        // F.1c Phase 4: mother/father UI state is derived from canonical on
        // render, so no imperative hydration is needed here (the selection the
        // derivation reads is applied to `testOptions` above).

        // Restore orderFormInfoData if not already set from location.state
        if (!orderFormInfoData) {
          const partial = mapCanonicalToOrderFormInfo(canonicalOrderData)
          setOrderFormInfoData(partial as OrderFormInfoData)
          setPatientInfo(getPatientInfoFromForm(partial as OrderFormInfoData))
        }
        setPatientError(null)
        setSessionHydrationDone(true)
        traceOrderStart('hydrate GET apply local state done', {
          sessionId,
          existingTestType,
          skippedOrderFormInfoMerge: Boolean(orderFormInfoData),
        })
        Sentry.logger.info('Form state hydrated from session on refresh', { sessionId })

        // Async HPO label resolution — only for code-only labels (live extract
        // already ships human-readable names; fan-out search hits rate limits).
        // Write back into canonical clinicalDiagnosis too — the editor reads
        // that list, not the indications mirror.
        const needsLabelLookup = existingPhenotypes.some(
          (term) => isHpoCode(term.label) && (!term.node?.lbl || isHpoCode(term.node.lbl))
        )
        if (needsLabelLookup) {
          try {
            await Sentry.startSpan(
              { op: 'hpo.update_labels', name: 'Update HPO Labels for Indications' },
              async () => {
                const getTermById = (id: string) => hpoAPIClient.getHPOTermById(id)
                const needsLabel = (term: CategorizedHPOOption) =>
                  isHpoCode(term.label) && (!term.node?.lbl || isHpoCode(term.node.lbl))
                const resolvedById = new Map(
                  // One failed lookup leaves only that term unnamed; the editor tries it again when it opens.
                  await mapWithConcurrency(existingPhenotypes.filter(needsLabel), 2, async (term) => {
                    try {
                      return [term.id, await resolveHpoLabel(term, getTermById)] as const
                    } catch (lookupError) {
                      Sentry.captureException(lookupError)
                      return [term.id, null] as const
                    }
                  })
                )
                // The clinician may have edited phenotypes while labels resolved; relabel what is there now.
                const currentDiagnosis = appStore.getState().canonicalOrder.canonicalOrder?.reason?.clinicalDiagnosis ?? []
                const updatedDiagnosis = currentDiagnosis.map((term) => {
                  const resolved = needsLabel(term) ? resolvedById.get(term.id) : undefined
                  if (!resolved || resolved.label === term.label) return term
                  // Only the name changes: the review and its citations are keyed by the term's id.
                  return {
                    ...term,
                    label: resolved.label,
                    node: {
                      ...term.node,
                      id: term.node?.id ?? term.id,
                      lbl: resolved.label,
                      type: term.node?.type ?? 'PhenotypicFeature',
                    },
                  }
                })
                if (updatedDiagnosis.every((term, index) => term === currentDiagnosis[index])) return
                dispatch(mergeReason({ clinicalDiagnosis: updatedDiagnosis }))
                setIndications({
                  phenotypes: updatedDiagnosis.map((term) => ({
                    id: term.id,
                    name: term.label,
                    hpoCode: term.id,
                    type: mapCategoryToType(term.category),
                  })),
                })
              }
            )
          } catch (error) {
            Sentry.captureException(error)
          }
        }
      } catch (error) {
        if (error instanceof OrderingSessionGoneError) {
          handleSessionGoneRef.current()
          setSessionHydrationDone(true)
          return
        }
        traceOrderStart('hydrate GET error', { sessionId, message: error instanceof Error ? error.message : String(error) })
        Sentry.captureException(error, { tags: { operation: 'order.session.hydrate' } })
        setSessionHydrationDone(true)
      }
    }
    void hydrateFromSession()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId])


  useEffect(() => {
    if (hasFetchedPatient.current) {
      return
    }

    const loadPatientInfo = async () => {
      hasFetchedPatient.current = true

      // Check if we're loading an existing order by ID (standalone mode)
      if (authFlow === 'standalone' && standaloneOrderId) {
        try {
          const scope = await checkResourceScope('order', standaloneOrderId)
          if (scope === 'not_found') {
            setPatientError('Order not found. Please create a new order.')
            return
          }

          const caller = getCallerName()
          const order = await getOrderById(standaloneOrderId, caller)
          if (!order) {
            setPatientError('Order not found. Please create a new order.')
            return
          }

          // Extract orderFormInfoData and requisitionFormData from json_payload
          const jsonPayload = order.json_payload as {
            orderFormInfo?: OrderFormInfoData
            requisitionFormData?: PartialRequisitionFormData
          }

          if (jsonPayload?.orderFormInfo) {
            // Convert string dates to Date objects (JSON deserialization returns strings)
            let orderFormInfo: OrderFormInfoData = { ...jsonPayload.orderFormInfo }
            if (orderFormInfo.patient?.birthDate) {
              // If birthDate is a string, convert it to a Date object
              if (typeof orderFormInfo.patient.birthDate === 'string') {
                orderFormInfo.patient.birthDate =
                  parseDateOnlyToLocalDate(orderFormInfo.patient.birthDate) ?? new Date(orderFormInfo.patient.birthDate)
              }
            }

            // Standalone draft from workspace: set orderingProvider.id from Auth0 when missing
            if (auth0User && !orderFormInfo.orderingProvider?.id) {
              const providerId = auth0User.sub || auth0User.email
              if (providerId) {
                orderFormInfo = {
                  ...orderFormInfo,
                  orderingProvider: {
                    ...orderFormInfo.orderingProvider,
                    id: providerId,
                    name: orderFormInfo.orderingProvider?.name ?? {
                      firstName: auth0User.given_name ?? '',
                      lastName: auth0User.family_name ?? '',
                    },
                    roleTitle: orderFormInfo.orderingProvider?.roleTitle ?? '',
                    phone: orderFormInfo.orderingProvider?.phone ?? '',
                    email:
                      orderFormInfo.orderingProvider?.email ??
                      orderFormInfo.medicalProfessionalInfo?.email ??
                      auth0User.email ??
                      '',
                    npi:
                      orderFormInfo.orderingProvider?.npi ??
                      orderFormInfo.medicalProfessionalInfo?.npi ??
                      '',
                  },
                }
              }
            }

            setOrderFormInfoData(orderFormInfo)
            setPatientInfo(getPatientInfoFromForm(orderFormInfo))

            // Adapt legacy json_payload to canonical so the ordering session has correct state.
            if (jsonPayload.requisitionFormData) {
              const ehrCtx = null
              const canonical = legacyTrfPayloadToCanonical(
                { orderFormInfo, requisitionFormData: jsonPayload.requisitionFormData },
                ehrCtx
              )
              dispatch(setCanonicalOrder(canonical))
            }
          } else {
            setPatientError('Order data is incomplete. Please create a new order.')
            return
          }

          setPatientError(null)
          return
        } catch (error) {
          Sentry.captureException(error, { tags: { operation: 'order.load_by_id' } })
          setPatientError(error instanceof Error ? error.message : 'Failed to load order')
          return
        }
      }

      // Check if patient data was passed from standalone flow (New Order modal)
      if (authFlow === 'standalone' && standalonePatientData) {
        // Use the patient data passed from the New Order modal
        setOrderFormInfoData(standalonePatientData)
        setPatientInfo(getPatientInfoFromForm(standalonePatientData))
        setPatientError(null)
        return
      }

      // Only load patient data from Epic flow
      if (authFlow !== 'epic' || !state) {
        // Standalone flow without patient data or order ID - this shouldn't happen, redirect back
        setPatientError('No patient data available. Please create a new order.')
        return
      }

      setPatientError(null)

      try {
        // Fetch the proband demographics and the legal guardian contact together
        // so the Guardian Contact section is populated as part of the initial
        // page load rather than a separate, later call. `guardianPrefillLoading`
        // blocks editing that section; it is released either here (when there is
        // no guardian to apply) or by `commitGuardianPrefill` after the value is
        // durably persisted with the session — never in between, so a provider
        // save can't race the pending apply.
        setGuardianPrefillLoading(true)
        const [data, guardianOutcome] = await Promise.all([
          extractFormOrderData(state),
          lookupGuardianContact(state).catch((err) => {
            Sentry.captureException(
              err instanceof Error ? err : new Error('Guardian contact prefill failed'),
              { tags: { operation: 'order.guardian.prefill_on_load' } }
            )
            return { status: 'error' as const, message: 'Guardian contact lookup failed.' }
          }),
        ])
        setOrderFormInfoData(data)
        setPatientInfo(getPatientInfoFromForm(data))
        // Stash the fetched guardian; the create/resume session effect merges it
        // once canonical exists (setGuardianContact no-ops before then) and
        // consumes the ref + releases the lock via `commitGuardianPrefill` only
        // after the session is durably persisted.
        pendingEpicGuardianRef.current =
          guardianOutcome.status === 'found' ? guardianOutcome.guardian : null
        if (guardianOutcome.status === 'found') {
          Sentry.logger.info('Guardian contact fetched from Epic FHIR on load', {
            totalCount: guardianOutcome.totalCount,
            relationshipToPatient: guardianOutcome.guardian.relationshipToPatient ?? null,
          })
        } else {
          // Nothing to apply → release the lock now (the session effect won't).
          setGuardianPrefillLoading(false)
        }
      } catch (error) {
        Sentry.captureException(error)
        setPatientError(error instanceof Error ? error.message : 'Failed to load patient details')
        // Patient load failed → no session/canonical will be seeded, so release
        // the lock here and drop any stashed guardian so it can't apply later.
        pendingEpicGuardianRef.current = null
        setGuardianPrefillLoading(false)
      }
    }

    // When the page loads with a sessionId already in the URL (browser refresh),
    // skip the normal patient-data loading — the session hydration effect handles it.
    if (sessionId) {
      hasFetchedPatient.current = true
      return
    }

    loadPatientInfo()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, authFlow, standalonePatientData, standaloneOrderId])

  useEffect(() => {
    if (!config.testTypes?.length) {
      return
    }
    if (!testOptions.testType) {
      return
    }
    const orderTestTypeFromDb = storedCanonical?.order?.selectedOption
    const isValid = config.testTypes.some((testType) => testType.value === testOptions.testType)
    const matchesSessionLoaded =
      sessionLoadedTestTypeRef.current != null && testOptions.testType === sessionLoadedTestTypeRef.current
    const isFromExistingOrder =
      (orderTestTypeFromDb != null && testOptions.testType === orderTestTypeFromDb) || matchesSessionLoaded

    if (isFromExistingOrder && !isValid) {
      return
    }

    if (!isValid) {
      traceOrderStart('testType validation reset', {
        testOptionsTestType: testOptions.testType,
        orderTestTypeFromDb,
        matchesSessionLoaded,
        isFromExistingOrder,
      })
      setTestOptions((prev) => ({ ...prev, testType: hasConfiguredDefaultTestType ? defaultTestType : '' }))
    }
  }, [
    config.testTypes,
    defaultTestType,
    storedCanonical?.order?.selectedOption,
    hasConfiguredDefaultTestType,
    testOptions.testType,
  ])

  // GEN-3xxx: Backfill `order.selectedOption` into the canonical once the
  // session/canonical exists. On the create path the canonical is seeded from
  // `testOptions.testType` a single time (initCanonicalFromOrderFormInfo), and
  // the only later writers — `mergeOrderSelection` /
  // `applySequencingOptionsChangeToCanonical` — silently no-op while
  // `storedCanonical` is still null (session round-trip in flight). A test type
  // chosen in the Sequencing Options modal before Redux was seeded was
  // therefore dropped, persisting `selectedOption: null` and disabling
  // comparator editing on reload. This reconciles the local selection into the
  // canonical whenever the canonical carries no test type but the form does;
  // the autosave effect then persists it. Only writes when the local value is a
  // configured, valid test type, so it never clobbers a real selection.
  useEffect(() => {
    if (!storedCanonical) return
    const localTestType = testOptions.testType?.trim()
    if (!localTestType) return
    const canonicalSelectedOption = storedCanonical.order?.selectedOption?.trim()
    if (canonicalSelectedOption) return
    const isValid = config.testTypes?.some((testType) => testType.value === localTestType)
    if (!isValid) return
    // D2 (GEN-5383 arch-review): while a deferred Epic create is armed and no
    // session exists, this backfill is a SYSTEM write. If it would only write the
    // untouched CONFIG DEFAULT it must NOT run — that would diverge the deferred
    // baseline and flip the (D1-nulled) seed to "touched", firing a bare-seed POST.
    // A genuine provider pick comes through `mergeOrderSelection` directly, so it
    // doesn't rely on this effect. Once a session exists, behaves as before;
    // non-Epic / standalone paths never arm, so they're unaffected.
    const isUntouchedConfigDefaultBackfill =
      hasConfiguredDefaultTestType && localTestType === defaultTestType?.trim()
    if (
      pendingCreateArmedRef.current &&
      !sessionIdRef.current &&
      isUntouchedConfigDefaultBackfill
    ) {
      return
    }
    dispatch(
      mergeOrderSelection({
        selectedOption: localTestType,
        labName: storedCanonical.order?.labName ?? getLabName(config) ?? null,
      })
    )
  }, [dispatch, storedCanonical, testOptions.testType, config, hasConfiguredDefaultTestType, defaultTestType])

  const handleLogout = () => {
    Sentry.startSpan(
      {
        op: 'ui.click',
        name: 'Logout from Order Page',
      },
      (span) => {
        span.setAttribute('auth.flow', 'standalone')
        auth0Logout({
          logoutParams: {
            returnTo: window.location.origin + '/login',
          },
        })
      }
    )
  }

  const indicationsFixTarget = indicationsAttentionTarget(
    hasClinicalSummary,
    hasIndications,
    clinicalSummaryReviewBlocksSubmit
  )
  const orderStartSectionLinks: AlertSectionLink[] = (() => {
    const sectionLinks: AlertSectionLink[] = []

    // R5 (side-nav model): names and navigation mirror the side nav —
    // patient sections deselect any open comparator and scroll to their
    // anchor; each incomplete comparator is listed once by relationship
    // and selects that comparator's detail view on click.
    if (sideNavEnabled) {
      if (!hasSelectedTestType) {
        sectionLinks.push({
          id: 'sequencing-options',
          name: 'Sequencing Options',
          onClick: () => openSectionEditor('order-section-sequencing', () => setTestOptionsModalOpen(true)),
        })
      }
      if (duoComparatorUnset) {
        sectionLinks.push({
          id: 'sequencing-options-comparator',
          name: 'Comparator sample',
          onClick: () => openSectionEditor('order-section-sequencing', () => setTestOptionsModalOpen(true)),
        })
      }
      // The summary text and the phenotypes are edited in one modal, so both
      // conditions share a single link rather than offering the clinician two
      // identically named routes to the same place.
      if (!hasClinicalSummary || indicationsFixTarget === 'clinical-summary') {
        sectionLinks.push({
          id: 'clinical-summary',
          name: 'Clinical Summary',
          onClick: () => openSectionEditor('order-section-clinical', () => setClinicalModalOpen(true)),
        })
      }
      if (familyHistoryRequired && familyHistoryIncluded && !hasFamilyHistorySummary) {
        sectionLinks.push({
          id: 'family-history',
          name: 'Family History',
          onClick: () =>
            openSectionEditor('order-section-family-history', () => setFamilyHistorySummaryModalOpen(true)),
        })
      }
      // R5 (decoupled): one link per incomplete comparator (any
      // relationship), labeled by its relationship. Selecting it opens
      // that comparator's detail view (auto-scrolls). This iterates the
      // actual `comparators[]` rows instead of the fixed mother/father
      // slots, so an "Other Comp" (e.g. Grandfather) is surfaced and a
      // trio without a biological Father is no longer flagged for a
      // Father that doesn't exist.
      const incidentalApplicableForAlert = incidentalConsentGated(comparatorParentConsentConfigs)
      // Numbered display labels (e.g. "Sibling 2") from the same source the
      // side-nav uses, so the "needs your input" links match the nav.
      const comparatorLabelById = new Map(comparatorGroups.map((g) => [g.id, g.label]))
      for (const c of storedCanonical?.comparators ?? []) {
        if (c == null || c.relationship === 'PROBAND' || typeof c.id !== 'string' || c.id.trim() === '') {
          continue
        }
        if (
          FormCompletionChecker.isCanonicalComparatorComplete(c, {
            incidentalApplicable: incidentalApplicableForAlert,
            probandResearchContactConsent: storedCanonical?.patient?.researchContactConsent === true,
            nyRetentionRequireInteraction: consentInteractionGates.nyRetentionRequireInteraction,
            researchContactRequireInteraction: consentInteractionGates.researchContactRequireInteraction,
          })
        ) {
          continue
        }
        const comparatorId = c.id
        sectionLinks.push({
          id: `comparator-${comparatorId}`,
          name:
            comparatorLabelById.get(comparatorId) ||
            formatComparatorRelationshipLabel(c.relationship) ||
            'Comparator',
          onClick: () => setSelectedComparatorId(comparatorId),
        })
      }
      // Consent ("Additional Options") is listed before Guardian Contact to
      // match the section stack order (consent gates whether guardian is
      // required).
      if (probandConsentNeedsAttention) {
        sectionLinks.push({
          id: 'consent',
          name: 'Additional Options',
          onClick: () => scrollAndPulseSection(PROBAND_INCIDENTAL_FINDINGS_ANCHOR_ID),
        })
      }
      if (showGuardianContact && guardianContactNeedsAttentionForResearch) {
        sectionLinks.push({
          id: 'guardian-contact',
          name: 'Guardian Contact',
          onClick: () => openSectionEditor('order-section-guardian', () => setGuardianContactModalOpen(true)),
        })
      }
    } else {
      // Legacy single-column model: open the relevant modal directly.
      if (!hasSelectedTestType) {
        sectionLinks.push({
          id: 'sequencing-options',
          name: 'Sequencing Options',
          onClick: () => setTestOptionsModalOpen(true),
        })
      }
      if (duoComparatorUnset) {
        sectionLinks.push({
          id: 'sequencing-options-comparator',
          name: 'Comparator sample (mother or father)',
          onClick: () => setTestOptionsModalOpen(true),
        })
      }
      if (!hasClinicalSummary || indicationsFixTarget === 'clinical-summary') {
        sectionLinks.push({
          id: 'clinical-summary',
          name: 'Clinical Summary',
          onClick: () => setClinicalModalOpen(true),
        })
      }
      if (familyHistoryRequired && familyHistoryIncluded && !hasFamilyHistorySummary) {
        sectionLinks.push({
          id: 'family-history',
          name: 'Family History',
          onClick: () => setFamilyHistorySummaryModalOpen(true),
        })
      }
      // MOTHER/FATHER migration #6: one fix-me link per incomplete selected
      // parent, driven by `parentAttentionRows` (iterates the selected parent
      // relationships) rather than two hardcoded mother/father branches.
      for (const row of parentAttentionRows) {
        if (row.needsAttention) {
          sectionLinks.push({
            id: row.anchorId,
            name: row.label,
            onClick: () => openComparatorEditorForParent(row.side),
          })
        }
      }
      if (!showParentCards && guardianContactNeedsAttentionForResearch) {
        sectionLinks.push({
          id: 'guardian-contact',
          name: 'Guardian Contact',
          onClick: () => setGuardianContactModalOpen(true),
        })
      }
      if (probandConsentNeedsAttention) {
        sectionLinks.push({
          id: 'incidental-findings',
          name: 'Additional Options',
          onClick: scrollToProbandIncidentalFindings,
        })
      }
    }

    return sectionLinks
  })()

  const incompleteSectionsBanner = canSubmit ? (
    <div className="flex flex-col gap-2">
      {showReadyBanner && (
        <AlertBanner
          onClose={() => setShowReadyBanner(false)}
          useInflectionIcon={true}
          introText={copyWithoutSigning('Ready to complete and sign', isTrfSignatureDocusignEnabled(config))}
        />
      )}
    </div>
  ) : (
    showBanner &&
    orderStartSectionLinks.length > 0 && (
      <AlertBanner
        onClose={() => setShowBanner(false)}
        useInflectionIcon={true}
        sectionLinks={orderStartSectionLinks}
        outroText="need your input to complete this order."
      />
    )
  )

  const epicDiscardSurvivedReload =
    authFlow === 'epic' && !sessionId && isDiscardMarkerSet(patientLookupId ?? readEpicLaunchPatientId(state))
  if (epicDiscarded || epicDiscardSurvivedReload) {
    return <EpicOrderDiscarded />
  }

  return (
    <div className={authFlow === 'standalone' ? 'page-container-standalone' : 'page-container'}>
      {authFlow === 'standalone' && (
        <Navbar
          userAvatar={auth0User?.picture}
          userName={auth0User?.name}
          onLogout={handleLogout}
          showNavLinks={true}
          organizationName={getClientName(config)}
        />
      )}
      <div className={`flex w-full flex-col gap-4 ${authFlow === 'standalone' ? 'px-6' : ''}`}>
        <OrderHeader
          patient={patientInfo}
          steps={orderSteps}
          currentStep={0}
          mode="start"
          canSubmit={canSubmit}
          onSubmit={handleSubmitOrder}
          isLoading={isSubmittingForReview}
          isNavigationDisabled={isSubmittingForReview}
          showHelp={Boolean(config.featureFlags?.help)}
          containerClassName={authFlow === 'standalone' ? '-mx-6 border-b' : 'rounded-base border'}
          navToggle={
            sideNavEnabled ? (
              <OrderNavToggle
                drawerOpen={sideNavDrawerOpen}
                onOpen={() => setSideNavDrawerOpen(true)}
              />
            ) : undefined
          }
          onEditPatient={
            authFlow === 'standalone' && orderFormInfoData?.patient
              ? () => setPatientDemographicsEditOpen(true)
              : undefined
          }
          onDiscardOrder={sessionId ? openDiscardConfirm : undefined}
        />

        <EpicSessionExpiryWarning />

        {mrnCollision && (
          <AlertBanner variant="error" useInflectionIcon={true}>
            {`${mrnCollision.labels[0]} and ${mrnCollision.labels[1]} use the same MRN (${mrnCollision.mrn}). Each person needs a different MRN before submitting the order.`}
          </AlertBanner>
        )}
        {patientError && (
          <AlertBanner onClose={() => setPatientError(null)} useInflectionIcon={true}>
            {patientError}
          </AlertBanner>
        )}
        {orderActionError && (
          <AlertBanner onClose={() => setOrderActionError(null)} useInflectionIcon={true}>
            {orderActionError}
          </AlertBanner>
        )}
        {discardError && (
          <AlertBanner onClose={clearDiscardError} useInflectionIcon={true} variant="error">
            {discardError}
          </AlertBanner>
        )}

        <div className={sideNavEnabled ? 'flex w-full items-start gap-4' : 'contents'}>
          {sideNavEnabled && (
            <div className="sticky top-(--order-sticky-top) z-30 shrink-0 self-start lg:max-h-[calc(100vh-var(--order-sticky-top)-1rem)] lg:overflow-y-auto">
              <OrderSideNav
                sections={sideNavSectionsForMergedClinicalSummary(
                  sideNavSections,
                  clinicalSummaryReviewBlocksSubmit
                )}
                comparatorGroups={comparatorGroups}
                selectedComparatorId={selectedComparatorId}
                onSelectComparator={setSelectedComparatorId}
                onAddComparator={() => setAddComparatorOpen(true)}
              addComparatorDisabled={comparatorLimitReached}
              addComparatorDisabledReason={comparatorLimitReachedReason ?? undefined}
              drawerOpen={sideNavDrawerOpen}
              onDrawerOpenChange={setSideNavDrawerOpen}
            />
            </div>
          )}
          <div className="flex min-w-0 flex-1 flex-col gap-4">
          {clinicalTextError && (
            <AlertBanner variant="error">{clinicalTextError}</AlertBanner>
          )}
          {incompleteSectionsBanner}
          {sideNavEnabled ? (
            <div className="sticky top-(--order-header-h) z-20 -mx-1 -mt-4 flex items-center gap-3 bg-background-variant-gray dark:bg-gray-950 px-1 pb-2 pt-4">
              <h2 className="text-xl font-semibold tracking-tight text-text-heading dark:text-white">
                Patient
                {orderStartPatientName ? (
                  <span className="text-text-body-subtle dark:text-gray-500"> · {orderStartPatientName}</span>
                ) : null}
              </h2>
            </div>
          ) : null}
          <>
          <section id="order-section-sequencing" className="scroll-mt-28">
          <TestOptionsCard
            testTypeLabel={selectedTestTypeLabel}
            turnaroundDescription={turnaroundSequencingSubtitle}
            familySamples={{
              title: familySamplesTitle,
              description: familySamplesDescription,
            }}
            sampleType={testOptions.sampleType}
            onEdit={() => setTestOptionsModalOpen(true)}
            // Embedded "Add Comparator" pill (mirrors the Indications card's
            // "Add Phenotypes" action). Uses the side-nav comparator-add flow
            // and its cap-based disabled state.
            onAddComparator={
              sideNavEnabled
                ? () => {
                    if (comparatorLimitReached) return
                    setAddComparatorOpen(true)
                  }
                : undefined
            }
            addComparatorDisabled={comparatorLimitReached}
            addComparatorDisabledReason={comparatorLimitReachedReason ?? undefined}
            hasSelectedTestType={hasSelectedTestType}
            extraNeedsAttention={duoComparatorUnset}
            r5SpeedAndSampleOnly={sideNavEnabled}
            required={sideNavEnabled}
          />
          </section>
          <section id="order-section-clinical" className="scroll-mt-28">
          <ClinicalSummaryCard
            summary={clinicalSummary.summary}
            differentialDiagnosis={clinicalSummary.differentialDiagnosis}
            priorGeneticTesting={clinicalSummary.priorGeneticTesting}
            statusMessage={(() => {
              const count = storedCanonical?.reason?.clinicalDiagnosis?.length ?? 0
              return count > 0 ? `${count} phenotype${count === 1 ? '' : 's'}` : undefined
            })()}
            onEdit={() => setClinicalModalOpen(true)}
            needsAttention={!hasClinicalSummary}
            required={sideNavEnabled && Boolean(config.validationRules?.requireClinicalSummary)}
          />
          </section>

          <section id="order-section-family-history" className="scroll-mt-28">
          <FamilyHistoryCard
            hasData={familyHistoryIncluded && hasFamilyHistorySummary}
            summary={familyHistorySummary.summary}
            details={familyHistoryDetails}
            onEdit={() => setFamilyHistorySummaryModalOpen(true)}
            needsAttention={familyHistoryNeedsAttention}
            required={sideNavEnabled && familyHistoryRequired}
            statusMessage={familyHistoryDisplayMessage}
            statusVariant={familyHistoryStatusVariant}
            showEmptyState={familyHistoryRequired || familyHistoryIncluded}
          />
          </section>

          {/* Consent ("Additional Options") renders before Guardian Contact:
              the proband's consent choices (research-contact opt-in /
              incidental findings) gate whether Guardian Contact is required,
              so consent is collected first. */}
          {storedCanonical && comparatorConsentAnyApplicable(comparatorParentConsentConfigs) ? (
            <div id={PROBAND_INCIDENTAL_FINDINGS_ANCHOR_ID} className="scroll-mt-24">
              {sideNavEnabled ? (
                // R5: a single read-only Additional Options summary card (like
                // comparators); editing happens in <PatientConsentModal>.
                <PatientConsentCard
                  configs={comparatorParentConsentConfigs}
                  patient={storedCanonical.patient}
                  onEdit={() => setPatientConsentModalOpen(true)}
                  needsAttention={probandConsentNeedsAttention}
                  required
                />
              ) : (
                <ProbandConsentSections
                  configs={comparatorParentConsentConfigs}
                  patient={storedCanonical.patient}
                  onPatch={(patch) => dispatch(mergePatient(patch))}
                  showValidationErrors={probandConsentNeedsAttention}
                />
              )}
            </div>
          ) : null}

          {showGuardianContact && (
            <section id="order-section-guardian" className="scroll-mt-28">
            {primaryGuardianComparator ? (
              <GuardianContactCard
                firstName={primaryGuardianComparator.demographic?.firstName}
                lastName={primaryGuardianComparator.demographic?.lastName}
                dateOfBirth={primaryGuardianComparator.demographic?.dateOfBirth}
                email={primaryGuardianComparator.demographic?.email}
                phone={primaryGuardianComparator.demographic?.phone}
                showPrimaryGuardianBadge
                relationshipLink={{
                  label:
                    formatComparatorRelationshipLabel(primaryGuardianComparator.relationship) ||
                    'Comparator',
                  // Read-only mirror: route straight to the comparator's
                  // Demographics section so its contact info stays the single
                  // source of truth.
                  onClick: () =>
                    setComparatorSectionEdit({
                      id: primaryGuardianComparator.id as string,
                      group: 'demographics',
                    }),
                }}
                needsAttention={
                  guardianContactNeedsAttentionForResearch || !primaryGuardianComparatorComplete
                }
                statusMessage={
                  guardianContactNeedsAttentionForResearch
                    ? `Edit ${
                        formatComparatorRelationshipLabel(primaryGuardianComparator.relationship) ||
                        'comparator'
                      }'s contact for research contact`
                    : !primaryGuardianComparatorComplete
                      ? 'Incomplete'
                      : `Uses ${
                          formatComparatorRelationshipLabel(primaryGuardianComparator.relationship) ||
                          'comparator'
                        }'s contact`
                }
                statusVariant="info"
              />
            ) : (
              <GuardianContactCard
                {...guardianContactCardFieldsFromCanonical(storedCanonical?.guardianContact)}
                onEdit={guardianPrefillLoading ? undefined : () => setGuardianContactModalOpen(true)}
                needsAttention={!guardianPrefillLoading && guardianContactNeedsAttentionForResearch}
                statusMessage={
                  guardianPrefillLoading
                    ? 'Retrieving guardian contact from the EHR…'
                    : guardianContactNeedsAttentionForResearch
                      ? 'Guardian: add email or phone for research contact'
                      : undefined
                }
                statusVariant={
                  guardianPrefillLoading
                    ? 'neutral'
                    : guardianContactNeedsAttentionForResearch
                      ? 'info'
                      : undefined
                }
              />
            )}
            </section>
          )}

          {/* Legacy single-column family list. In the R5 side-nav layout,
              comparators are surfaced as left-nav groups + the detail view and
              are added only via "Add Comparator", so no mother/father cards are
              rendered in the patient context here (parity with OrderInProgress).
              A guardian's MOTHER/FATHER relationship is metadata on the Guardian
              Contact card and never auto-spawns a comparator card. */}
          {!sideNavEnabled &&
            showParentCards &&
            storedCanonical &&
            (() => {
              /**
               * `<ComparatorList>` is the family-section render path for the
               * legacy single-column layout. The projection synthesizes empty
               * items for any parent row the user has not filled yet
               * (`synthesizeEmptyForUnmatchedParents`), so the section never
               * goes blank.
               */
              const display = deriveOrderInProgressFamilyRows(storedCanonical)
              const projection = projectComparatorsToOrderInProgressRows(
                display,
                storedCanonical.comparators,
                storedCanonical,
                { synthesizeEmptyForUnmatchedParents: true, successStatusText: 'Complete' }
              )
              return (
                <ComparatorList
                  display={display}
                  parentItems={projection.parentItems}
                  renderGuardianRow={() => null}
                  resolveEditHandlerForParent={(item) => () =>
                    setComparatorEditState({
                      // A guardian-backed row is hydrated from
                      // `canonical.guardianContact` for display only. The
                      // comparator editor must still open on the stored family
                      // member (or an empty slot), so saving it never silently
                      // promotes the guardian into a sequenced comparator
                      // carrying details the provider never typed here.
                      comparator: item.guardianContactBacked
                        ? (getParentComparator(storedCanonical, item.editTarget) ?? {
                            relationship: item.comparator.relationship,
                          })
                        : item.comparator,
                    })
                  }
                />
              )
            })()}

          {/* GEN-3152 Phase 2: non-parent family members (e.g. a legal guardian
              who consents but is not sequenced) on family orders. Parents
              render above via <ComparatorList>; this lists the remaining
              comparators and offers an "Add family member" entry point that
              opens <ComparatorModal> restricted to non-parent relationships. */}
          {/* GEN-3152 Phase 2: non-parent family members (e.g. a legal guardian
              who consents but is not sequenced) on family orders. Legacy
              single-column layout only — in the R5 side-nav layout these are
              surfaced as left-nav comparator groups + the detail view and are
              added via the unified "Add Comparator" entry point. */}
          {!sideNavEnabled && showParentCards && storedCanonical
            ? (() => {
                const nonParentMembers = (storedCanonical.comparators ?? []).filter(
                  (c): c is Comparator =>
                    c != null &&
                    parentSideForComparator(c) === null &&
                    c.relationship !== 'PROBAND',
                )
                return (
                  <>
                    {nonParentMembers.map((member, i) => (
                      <ComparatorCard
                        key={`order-start-member-${member.id ?? member.demographic?.demographicId ?? i}`}
                        comparator={member}
                        onEdit={() => setMemberEditState({ comparator: member })}
                      />
                    ))}
                    <button
                      type="button"
                      disabled={comparatorLimitReached}
                      title={comparatorLimitReached ? (comparatorLimitReachedReason ?? undefined) : undefined}
                      aria-disabled={comparatorLimitReached}
                      onClick={() => {
                        if (comparatorLimitReached) return
                        setMemberEditState({ comparator: null })
                      }}
                      className="flex w-full items-center justify-center gap-2 rounded-base border border-dashed border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-3 text-sm font-medium text-brand-600 dark:text-brand-400 transition-colors hover:border-brand-600 dark:hover:border-brand-500 hover:bg-brand-50 dark:hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-border-base dark:disabled:hover:border-gray-700 disabled:hover:bg-white dark:disabled:hover:bg-gray-900"
                    >
                      + Add family member
                    </button>
                  </>
                )
              })()
            : null}

          </>

          {sideNavEnabled
            ? comparatorGroups.map((group) => {
                const rowComparator = resolveComparatorById(group.id)
                if (!rowComparator) return null
                const demo = rowComparator.demographic ?? {}
                const rowName = [demo.firstName, demo.lastName]
                  .filter((p) => p && String(p).trim())
                  .join(' ')
                  .trim()
                return (
                  <div
                    key={group.id}
                    className="mt-2 flex flex-col gap-4 border-t border-border-base dark:border-gray-700 pt-6"
                  >
                    <div className="sticky top-(--order-header-h) z-20 -mx-1 flex items-center gap-3 bg-background-variant-gray dark:bg-gray-950 px-1 pb-2 pt-4">
                      <h2 className="text-xl font-semibold tracking-tight text-text-heading dark:text-white">
                        {group.label}
                        {rowName ? <span className="text-text-body-subtle dark:text-gray-500"> · {rowName}</span> : null}
                      </h2>
                      <button
                        type="button"
                        onClick={() => {
                          setComparatorPendingRemoval({ id: group.id, comparator: rowComparator })
                          setComparatorRemovalConfirmOpen(true)
                        }}
                        aria-label={`Remove ${group.label}${rowName ? ` (${rowName})` : ''}`}
                        className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-border-error-subtle dark:border-error-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-sm font-medium text-text-fg-error dark:text-error-100 shadow-xs transition-colors hover:bg-error-50 dark:hover:bg-gray-800"
                      >
                        <TrashBin className="h-4 w-4" aria-hidden />
                        Remove Comparator
                      </button>
                    </div>
                    <ComparatorDetailView
                      comparator={rowComparator}
                      titleLabel={group.label}
                      hideOwnHeader
                      anchors={{
                        demographics: `comparator-${group.id}-demographics`,
                        clinical: `comparator-${group.id}-clinical`,
                        consent: `comparator-${group.id}-consent`,
                      }}
                      consentConfigs={comparatorParentConsentConfigs}
                      onEditDemographics={() =>
                        setComparatorSectionEdit({ id: group.id, group: 'demographics' })
                      }
                      onEditClinical={() =>
                        setComparatorSectionEdit({ id: group.id, group: 'clinical' })
                      }
                      onEditConsent={() =>
                        setComparatorSectionEdit({ id: group.id, group: 'consent' })
                      }
                      probandResearchContactConsent={
                        storedCanonical?.patient?.researchContactConsent === true
                      }
                    />
                  </div>
                )
              })
            : null}
          {/*
            Surface an Add Comparator button at the BOTTOM of the scrollable
            cards column on ALL widths (desktop + mobile), so a user can add a
            family member from the end of the cards without going to the left
            nav / hamburger. Same action + disabled state as the nav footer
            button. (On desktop this intentionally duplicates the nav's button.)
          */}
          {sideNavEnabled ? (
            <button
              type="button"
              disabled={comparatorLimitReached}
              title={comparatorLimitReached ? (comparatorLimitReachedReason ?? undefined) : undefined}
              aria-disabled={comparatorLimitReached}
              onClick={() => {
                if (comparatorLimitReached) return
                setAddComparatorOpen(true)
              }}
              className="mt-1 inline-flex w-full shrink-0 cursor-pointer items-center justify-center gap-2 rounded-full border border-border-brand dark:border-brand-500 bg-background-brand-softer dark:bg-gray-800 px-4 py-2.5 text-sm font-medium text-text-fg-brand dark:text-brand-400 shadow-xs transition-colors hover:bg-brand-100 dark:hover:bg-gray-700 disabled:cursor-not-allowed disabled:border-border-base dark:disabled:border-gray-700 disabled:bg-white dark:disabled:bg-gray-900 disabled:text-text-body dark:disabled:text-gray-400 disabled:opacity-50 disabled:hover:bg-white dark:disabled:hover:bg-gray-900"
            >
              <UserAdd className="h-4 w-4" />
              Add Comparator
            </button>
          ) : null}
          </div>
        </div>
      </div>

      <TestOptionsModal
        isOpen={testOptionsModalOpen}
        onClose={() => setTestOptionsModalOpen(false)}
        initialData={testOptions}
        onSave={(newTestOptions) => {
          // F.1c Phase 3: the ~150-line inline tier-transition state machine
          // (proband⇄duo⇄trio comparator + primary-guardian reconciliation) now
          // lives in `applySequencingOptionsChangeToCanonical`, the single
          // source of truth shared with OrderInProgress. Local mother/father UI
          // state is derived from canonical (Phase 4), so this handler only owns
          // the OrderStart-local concerns: the session test-type ref and the
          // `testOptions` form state. (Behavior note: the helper only re-pushes
          // proband `collectionType` when the sample type actually changed,
          // avoiding the duplicate-specimen risk of the old unconditional write.)
          sessionLoadedTestTypeRef.current = newTestOptions.testType ? newTestOptions.testType : null
          setTestOptions(newTestOptions)
          if (sideNavEnabled) {
            // R5: the Sequencing Options modal only changes the *speed* (category)
            // and proband sample type — comparators are decoupled and managed via
            // the side-nav. Running the parent-centric engine here would treat the
            // new category's proband representative as "leaving the comparator
            // family" (requestedParentCount === 0) and wipe mother/father/guardian.
            // Instead, just re-point the SKU to the new category at the current
            // comparator-derived tier and persist a sample-type change.
            const newCategory = newTestOptions.testType
              ? getTestTypeConfig(newTestOptions.testType, config)?.category
              : undefined
            if (newCategory) {
              const maxTier = getMaxParentSamplesForCategory(newCategory, config)
              const tier = Math.min(testingComparatorCount, maxTier)
              const desired =
                resolveTestTypeForCategoryTier(newCategory, tier, config) ?? newTestOptions.testType
              dispatch(
                mergeOrderSelection({ selectedOption: desired || null, labName: getLabName(config) || null })
              )
              // Sync local form state to the comparator-derived tier SKU too. The
              // modal returns the new category's Proband SKU; without this, the
              // displayed label (driven by `testOptions.testType`) would show
              // "Proband Only" until the next hydration, since the SKU-sync effect
              // early-returns once canonical already equals `desired`.
              if (desired && desired !== newTestOptions.testType) {
                setTestOptions((prev) => ({ ...prev, testType: desired }))
              }
            }
            if (testOptions.sampleType !== newTestOptions.sampleType) {
              dispatch(mergePatient({ collectionType: mapSampleTypeToCollectionType(newTestOptions.sampleType) || null }))
            }
            return
          }
          applySequencingOptionsChangeToCanonical({
            dispatch,
            canonical: storedCanonical,
            prev: testOptions,
            next: newTestOptions,
            config,
          })
        }}
        parentDataByRelationship={{
          MOTHER: biologicalMother,
          FATHER: biologicalFather,
        }}
        testTypeLabel={selectedTestTypeLabel}
        r5SpeedAndSampleOnly={sideNavEnabled}
        testingComparatorCount={testingComparatorCount}
        {...(() => {
          const next = getNextSectionStep('sequencing')
          return next ? { onContinue: next.open, continueLabel: `Continue to ${next.label}` } : {}
        })()}
      />

      {orderFormInfoData && newOrderPatientEditInitial ? (
        <NewOrderModal
          isOpen={patientDemographicsEditOpen}
          onClose={() => setPatientDemographicsEditOpen(false)}
          onSubmit={handleNewOrderModalPatientSave}
          userFirstName={auth0User?.given_name}
          userLastName={auth0User?.family_name}
          userEmail={auth0User?.email}
          organizationId={
            orderFormInfoData.organizationId ??
            orderFormInfoData.institution?.id ??
            orgId ??
            ''
          }
          mrnOrgResolution={{
            orgIdFromAuth: orgId,
            isEpicFlow: launchMode === 'epic_smart',
            epicOAuthState: state || null,
            additionalUuidCandidate:
              orderFormInfoData.organizationId ?? orderFormInfoData.institution?.id,
          }}
          mrnUniquenessContext={{
            initialMrn: newOrderPatientEditInitial.mrn,
            currentImdbPatientId: orderFormInfoData.patient.id,
          }}
          otherMrns={(storedCanonical?.comparators ?? [])
            .filter((c): c is Comparator => c != null && c.relationship !== 'PROBAND')
            .map((c) => ({
              label: (formatComparatorRelationshipLabel(c.relationship) || 'comparator').toLowerCase(),
              mrn: c.demographic?.mrn,
            }))}
          variant="edit"
          editInitialValues={newOrderPatientEditInitial}
          siteTrackingEnabled={orderSiteTrackingEnabled}
          siteOptions={organizationSiteNames}
        />
      ) : null}


      <ClinicalIndicationsModal
        isOpen={clinicalModalOpen}
        subtitle={clinicalModalSubtitle}
        onSaveAndClose={saveAndCloseClinicalModal}
        onDiscard={discardClinicalModal}
        lastSaveTime={draftLastSaveTime}
        isSaving={draftIsSaving}
        saveFailed={draftSaveFailed}
        {...(() => {
          const next = getNextSectionStep('clinical')
          return next
            ? {
                continueLabel: `Continue to ${next.label}`,
                onContinue: () => {
                  saveAndCloseClinicalModal()
                  next.open()
                },
              }
            : {}
        })()}
      >
        <ClinicalSummaryEditor
          key={clinicalSummaryEditorEpoch}
          controlRef={clinicalEditorControls}
          summary={clinicalSummary.summary}
          differentialDiagnosis={clinicalSummary.differentialDiagnosis}
          priorGeneticTesting={clinicalSummary.priorGeneticTesting}
          familyHistory={
            familyHistorySummary.includeFamilyHistory ? familyHistorySummary.summary : ''
          }
          clinicalTextBudget={probandClinicalTextBudget(getFulfillmentStrategy(config), storedCanonical)}
          attachmentsReferenceId={clinicalNoteAttachmentsEnabled && sessionId ? sessionId : null}
          phenotypeModel={getPhenotypeModel(config)}
          clinicalDiagnosis={storedCanonical?.reason?.clinicalDiagnosis ?? []}
          review={storedCanonical?.reason?.clinicalSummaryReview}
          buttonOnly={config.featureFlags?.buttonOnlyHpoExtraction === true}
          onSummaryChange={(summary) => {
            setClinicalSummary((current) => ({ ...current, summary }))
            dispatch(mergeClinical({ clinicalNote: summary }))
          }}
          onSupplementalChange={({ differentialDiagnosis, priorGeneticTesting }) => {
            setClinicalSummary((current) => ({ ...current, differentialDiagnosis, priorGeneticTesting }))
            dispatch(
              mergeClinical({
                differentialDiagnosis: differentialDiagnosis.trim() || null,
                priorGeneticTesting: priorGeneticTesting.trim() || null,
              })
            )
          }}
          onReviewChange={(clinicalDiagnosis, review) => {
            // Save & Close lets an extraction land after the editor closes; one from before a conflict reload is stale.
            if (clinicalSummaryEditorEpoch !== latestClinicalSummaryEditorEpoch.current) return
            // It merged the phenotypes as they were at close, so once the editor reopens it may land only on those.
            if (clinicalModalOpenings !== latestClinicalModalOpenings.current) {
              const reason = appStore.getState().canonicalOrder.canonicalOrder?.reason
              const atOpen = phenotypesAtClinicalModalOpen.current
              if (
                reason?.clinicalDiagnosis !== atOpen?.clinicalDiagnosis ||
                reason?.clinicalSummaryReview !== atOpen?.review
              ) {
                return
              }
            }
            dispatch(mergeReason({ clinicalDiagnosis, userInteracted: clinicalDiagnosis.length > 0 }))
            dispatch(setClinicalSummaryReview(review))
            setIndications({
              phenotypes: clinicalDiagnosis.map((term) => ({
                id: term.id,
                name: term.label,
                hpoCode: term.id,
                type: mapCategoryToType(term.category),
              })),
            })
          }}
          onUndoRestore={handleMergedSummaryUndo}
        />
      </ClinicalIndicationsModal>

      <FamilyHistorySummaryModal
        key="family-history-summary-modal"
        isOpen={familyHistorySummaryModalOpen}
        onClose={() => setFamilyHistorySummaryModalOpen(false)}
        initialData={familyHistorySummary}
        clinicalNote={clinicalSummary.summary}
        differentialDiagnosis={clinicalSummary.differentialDiagnosis}
        priorGeneticTesting={clinicalSummary.priorGeneticTesting}
        clinicalTextBudget={probandClinicalTextBudget(getFulfillmentStrategy(config), storedCanonical)}
        onSave={handleFamilyHistorySave}
        {...(() => {
          // Advance to the next visible patient section after saving family
          // history (Consent / Additional Options when present, otherwise
          // Guardian Contact — consent now precedes guardian in the chain).
          const next = getNextSectionStep('family-history')
          return next
            ? {
                continueLabel: `Continue to ${next.label}`,
                onContinue: (newData: FamilyHistorySummaryData) => {
                  handleFamilyHistorySave(newData)
                  next.open()
                },
              }
            : {}
        })()}
      />

      {(showGuardianContact || guardianContactRequiredForComparator) && (
        <GuardianContactModal
          key="guardian-contact-modal"
          isOpen={guardianContactModalOpen && !guardianPrefillLoading}
          requireContactChannel={guardianContactRequiredForComparator}
          onClose={() => {
            setGuardianContactModalOpen(false)
            setGuardianContactRequiredForComparator(false)
          }}
          initialCanonical={storedCanonical?.guardianContact}
          allowExtendedRelationships={sideNavEnabled}
          onSave={(canonical) => {
            // Mark the guardian as provider-touched so a deferred Epic create
            // does not re-apply the Epic prefill over this edit (e.g. a clear).
            guardianUserTouchedRef.current = true
            dispatch(setGuardianContact(canonical))
          }}
          {...(() => {
            const next = getNextSectionStep('guardian')
            return next ? { onContinue: next.open, continueLabel: `Continue to ${next.label}` } : {}
          })()}
        />
      )}

      {sideNavEnabled && (
        <ComparatorSideNavModals
          key="comparator-side-nav-modals"
          storedCanonical={storedCanonical}
          comparatorParentConsentConfigs={comparatorParentConsentConfigs}
          parentSampleTypes={parentSampleTypes}
          kitOrderingEnabled={kitOrderingEnabled}
          isEpicFlow={launchMode === 'epic_smart'}
          epicState={state}
          addComparatorOpen={addComparatorOpen}
          setAddComparatorOpen={setAddComparatorOpen}
          addComparatorRelationshipOptions={addComparatorRelationshipOptions}
          onAddComparatorSave={handleAddComparatorSave}
          comparatorSectionEdit={comparatorSectionEdit}
          setComparatorSectionEdit={setComparatorSectionEdit}
          onComparatorSectionSave={handleComparatorSectionSave}
          platformCollectsFor={() => comparatorPlatformCollects}
          guardianContactOnFile={orderGuardianContactOnFile}
          onRequestGuardianContact={requestGuardianContactForComparator}
          guardianModalOpen={guardianContactModalOpen}
          /* Draft flow stays lenient, except a primary-guardian comparator must
             supply a contact channel once the proband opts into research
             contact (it owns the proband's research-contact channel). */
          validationProfile={{
            probandResearchContactConsent: storedCanonical?.patient?.researchContactConsent === true,
          }}
          patientConsentModalOpen={patientConsentModalOpen}
          setPatientConsentModalOpen={setPatientConsentModalOpen}
          onPatientConsentSave={(patch) => dispatch(mergePatient(patch))}
          patientConsentShowValidationErrors={probandConsentNeedsAttention}
        />
      )}

      {/* Canonical comparator-edit modal. Mounts while `comparatorEditState`
          is non-null (set by the ComparatorList edit pencil). The save handler
          replicates every side effect of the legacy
          <BiologicalMother/FatherModal>.onSave path (primary-guardian sync,
          testOptions toggle, conditional canonical null-out vs upsert, local
          FamilyMember state sync via setBiologicalMother/Father, and
          syncGuardianFromPrimaryParent). Flag-independent as of F.1c
          (GEN-2999) Stage 1. */}
      {comparatorEditState ? (
        <ComparatorModal
          isOpen={true}
          {...comparatorGuardianProps}
          onClose={() => setComparatorEditState(null)}
          initialData={comparatorEditState.comparator}
          clinicalNoteBudget={comparatorClinicalNoteBudget(
            getFulfillmentStrategy(config),
            storedCanonical,
            comparatorEditState.comparator?.id
          )}
          /* Defense-in-depth: the parent edit-pencil only ever opens on a
             MOTHER / FATHER comparator (so `legacyFixedParentDefaults` locks
             the relationship select today), but we still restrict the option
             list by fulfillment strategy so an `inflection_test` (LSMC /
             SeqOne) order can never surface the extended relationship list here
             even if the lock invariant ever drifts. Mirrors the
             `memberEditState` modal below. */
          relationshipOptions={relationshipOptionsForStrategy(
            getFulfillmentStrategy(config),
            ADD_FAMILY_MEMBER_RELATIONSHIP_OPTIONS
          )}
          comparatorConsentConfig={comparatorParentConsentConfigs}
          enableClinicalFields
          enableSampleType
          requireClinicalIdentity
          legacyFixedParentDefaults
          patientResearchContactOptIn={storedCanonical?.patient?.researchContactConsent === true}
          probandResearchContactPrimaryGuardian={storedCanonical?.patient?.researchContactConsent === true}
          probandIncidentalFindingsSelection={storedCanonical?.patient?.incidentalFindingsSelection ?? null}
          parentSampleTypes={parentSampleTypes
            ?.map((c) => mapCollectionStringToSampleTypeStrict(c))
            .filter((s): s is SampleTypeOption => s !== null)}
          onSave={(updated) => {
            const sideKey: 'mother' | 'father' =
              updated.relationship === 'FATHER' ? 'father' : 'mother'
            const isTesting = updated.isTestingComparator !== false
            // Primary-guardian sync: clear the OTHER parent's primary flag.
            if (isTesting && updated.isPrimaryGuardian === true) {
              if (sideKey === 'mother') clearFatherPrimaryForTrio()
              else clearMotherPrimaryForTrio()
            }
            // C2.1c comparator-first write (GEN-3393): when testing is requested
            // we upsert the edited comparator; when testing is OFF we remove the
            // comparator entirely. Read back the parent as a slot view projected
            // from `comparators[]` for the local FamilyMember projection.
            if (isTesting) {
              dispatch(upsertComparator(updated))
            } else if (updated.id) {
              dispatch(removeComparator(updated.id))
            }
            const co = appStore.getState().canonicalOrder.canonicalOrder
            const newFamilyMember = canonicalParentToFamilyMember(
              getParentComparator(co, sideKey),
              isTesting,
              storedCanonical?.patient
            )
            // Keep the comparator selection in sync with the parent toggle.
            if (sideKey === 'mother' && motherSelected !== isTesting) {
              setTestOptions(withRelationshipSelection(testOptions, 'MOTHER', isTesting))
            } else if (sideKey === 'father' && fatherSelected !== isTesting) {
              setTestOptions(withRelationshipSelection(testOptions, 'FATHER', isTesting))
            }
            // Mother/father UI state is derived from canonical (Phase 4); the
            // upsert above already updated canonical, so the derivation picks up
            // the change on re-render. Guardian contact still syncs from the
            // freshly-projected member.
            syncGuardianFromPrimaryParent(
              newFamilyMember,
              sideKey === 'mother' ? 'MOTHER' : 'FATHER'
            )
            setComparatorEditState(null)
          }}
        />
      ) : null}

      {/* GEN-3152 Phase 2: add/edit a non-parent family member. The picker is
          restricted to non-parent relationships, so this flow only appends a
          comparator and never touches the legacy mother/father slots or the
          testOptions parent selection — hence the lean save handler. */}
      {memberEditState ? (
        <ComparatorModal
          isOpen={true}
          {...comparatorGuardianProps}
          onClose={() => setMemberEditState(null)}
          initialData={memberEditState.comparator}
          clinicalNoteBudget={comparatorClinicalNoteBudget(
            getFulfillmentStrategy(config),
            storedCanonical,
            memberEditState.comparator?.id
          )}
          relationshipOptions={relationshipOptionsForStrategy(
            getFulfillmentStrategy(config),
            ADD_FAMILY_MEMBER_RELATIONSHIP_OPTIONS
          )}
          comparatorConsentConfig={comparatorParentConsentConfigs}
          enableTestingToggle
          enableClinicalFields
          enableSampleType
          probandIncidentalFindingsSelection={storedCanonical?.patient?.incidentalFindingsSelection ?? null}
          parentSampleTypes={parentSampleTypes
            ?.map((c) => mapCollectionStringToSampleTypeStrict(c))
            .filter((s): s is SampleTypeOption => s !== null)}
          onSave={(updated) => {
            // R5 decoupling (Phase 2): the unified "Add family member" picker can
            // now add a biological parent (MOTHER / FATHER) directly as a
            // comparator, independent of the sequencing test type / tier. When
            // the saved member is a parent, route through the shared sequencing
            // engine so `orderType` + primary-guardian defaulting + legacy-slot
            // creation match the parent-pencil path, then upsert the entered
            // demographics. Non-parent members keep the lean append-only path.
            const side = parentSideForComparator(updated)
            if (side) {
              const nextSelection = withRelationshipSelection(
                testOptions,
                side === 'mother' ? 'MOTHER' : 'FATHER',
                true
              )
              applySequencingOptionsChangeToCanonical({
                dispatch,
                canonical: storedCanonical,
                prev: testOptions,
                next: nextSelection,
                config,
              })
              dispatch(upsertComparator(updated))
              setTestOptions(nextSelection)
            } else {
              dispatch(upsertComparator(updated))
            }
            setMemberEditState(null)
          }}
        />
      ) : null}

      <RestartOrderModal
        isOpen={restartPrompt !== null}
        onClose={() => resolveRestartChoice(null)}
        onChoose={resolveRestartChoice}
        cancelledAt={restartPrompt?.cancelledAt}
        cancelledByName={restartPrompt?.cancelledByName}
        isIntegratedTenant
      />

      <DiscardOrderConfirmDialog
        isOpen={discardConfirmOpen}
        onClose={closeDiscardConfirm}
        onConfirm={performDiscard}
        isSubmitting={isDiscarding}
        patientName={patientInfo.name}
        patientMrn={patientInfo.mrn ?? ''}
        draftOwnerDisplayName={draftOwnerDisplayName}
        draftUpdatedAt={draftSessionMeta.updatedAt}
        currentUserId={currentUserId}
        draftOwnerUserId={draftSessionMeta.ownerUserId}
        confirmReady={confirmReady}
      />

      <ConfirmDialog
        isOpen={comparatorRemovalConfirmOpen}
        onClose={() => {
          setComparatorRemovalConfirmOpen(false)
          setComparatorPendingRemoval(null)
        }}
        onConfirm={() => {
          setComparatorRemovalConfirmOpen(false)
          const pending = comparatorPendingRemoval
          setComparatorPendingRemoval(null)
          if (!pending) return
          dispatch(removeComparator(pending.id))
          // Only clear the live selection if it still points at the removed row.
          setSelectedComparatorId((current) => (current === pending.id ? null : current))
        }}
        title={`Remove ${
          (comparatorPendingRemoval &&
            formatComparatorRelationshipLabel(comparatorPendingRemoval.comparator.relationship)) ||
          'this comparator'
        } from this order?`}
        description="This cannot be undone."
        confirmLabel="Yes, remove"
        cancelLabel="No, keep"
      />

    </div>
  )
}
